/**
 * NewCaseWizard.jsx
 *
 * The new-case submission form. Spec: event_types_logic.md.
 *
 * Step 1 — General info: event type, reporter, clinic, client/title, date, staff.
 * Step 2 — Event-type-specific: type → treatment → subtype → code → category,
 *          requirements panel showing extra fields + typed uploads.
 *
 * Event type, treatment subtype, incident category and type-specific extras
 * persist via POST /api/cases (migration 007). Near-miss fields submit with
 * event_type = 'NearMiss'. The submitter's objective account (all event types)
 * is sent as IncidentDescription → the immutable incident_description column
 * (migration 016); medical_notes is left empty for admin-added notes.
 *
 * TODO (pre-release): drive vocabularies + requirements from admin_config
 * instead of the hardcoded eventVocabularies.js module; emit case_tasks rows
 * for matrix items the reporter couldn't satisfy.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { postCase, uploadCaseAttachment, getClinicContext } from '../api/client.js';
import { getSubSessionClinic, getSubSessionJobTitle, getSubSessionName, SUB_SESSION_EXPIRED, waitForSubSessionRenewal } from '../api/apiFetch.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { useAppConfig } from '../contexts/AppConfigContext.jsx';
import { filterByEventType, itemAvailableForEventType } from '../lib/configFilters.js';
import { getIncidentCategoriesForUser } from '../lib/eventConfig.js';
import { getTherapieMapsData } from '../api/blobClient.js';
import SearchableSelect from '../components/SearchableSelect.jsx';
import DateInput, { todayIso } from '../components/DateInput.jsx';
import { CharCountInput, CharCountTextarea } from '../components/CharCountField.jsx';
import DateTimeExtraField from '../components/DateTimeExtraField.jsx';
import SlotStateIcon from '../components/SlotStateIcon.jsx';
import {
  getAssignedClinics,
  isMultiClinicManager,
  isSingleClinicManager,
  defaultAssignedClinicName,
  assignedClinicGroups,
  clinicCountryGroup,
  clinicMatchesCountryFilter,
  resolveClinicManagerName,
} from '../utils/clinicAssignment.js';
import {
  subtypesForTreatment,
  SUBTYPE_OTHER,
  resolveTreatmentSubtypeValue,
} from '@shared/incidentCategoryCascade.js';
import {
  ATTACHMENT_CATEGORIES,
  ATTACHMENT_CATEGORY_LABELS,
  getRequirements,
  HQ_ONLY_REQUIREMENT_FIELDS,
  HQ_ONLY_REQUIREMENT_UPLOADS,
  TYPED_TEXT_CATEGORIES,
  treatmentHasCodes,
  submitEvidenceRequired,
  submitRequiredFields,
  reviewDateRequired,
  categoryNeedsMetadata,
  FIELD_META,
  nearMissCategoriesFor,
  NEAR_MISS_REPORTING_FUNCTIONS,
  NEAR_MISS_ASSIGNEES,
  NEAR_MISS_SEVERITY_TIERS,
  CLINIC_INCIDENT_TYPES,
  CLINIC_INCIDENT_PERSON_STATUS,
} from '../lib/eventVocabularies.js';
import { validateStep1, validateStep2, REVIEW_DATE_HELP, SEVERITY_NOT_CONFIGURED } from '../lib/wizardValidation.js';
import {
  categoryToken,
  previewFileName,
  buildUploadFile,
} from '../lib/attachmentNaming.js';
import { attachmentMetadataLine } from '../lib/attachmentMetadata.js';
import { isoToDmy } from '../lib/dates.js';
import TreatmentGuide from '../components/TreatmentGuide.jsx';
import { getTreatmentGuide } from '../lib/treatmentGuides.js';
import TypedTextFileModal from '../components/TypedTextFileModal.jsx';
import {
  REFUSAL_REGISTER_CATEGORY, REFUSAL_REGISTER_FUNCTION, REFUSAL_TS_KEYS, refusalDateOf,
} from '@shared/refusalRegister.js';
import SlotUploadModal from '../components/SlotUploadModal.jsx';
import YesNoSwitch from '../components/YesNoSwitch.jsx';
import { clinicMeta } from '../utils/clinicOptions.jsx';

// ── Form shape ────────────────────────────────────────────────────────────
const EMPTY_FORM = {
  // Step 1 — common
  Title:                        '',
  Clinic:                       '',
  Region:                       '',
  Country:                      '',
  DateofIncident:               '',
  ClientPhoneNumber:            '',
  StaffMemberConcerned:         '',
  ClinicManagerResponsible:     '',
  // Step 2 — event header (persisted via existing fields)
  EventType:                    'Treatment',
  Department:                   '', // = treatment_type (LHR/AWI/Other)
  Severity:                     '',
  ServiceCarriedOut:            '',
  MedicalNotes:                 '',
  // Step 2 — prototype-only (not persisted)
  TreatmentSubtype:             '', // Toxin / Filler / Other for AWI
  TreatmentSubtypeOther:        '',
  IncidentCategory:             '',
  IncidentCategoryOther:        '',
  TypeSpecific:                 {}, // { field_key: value, ... }
  // Step 2 — Treatment: client review date (Code 3–5 only; cases.review_date)
  ReviewDate:                   '',
  // Step 2 — Treatment: consent-form waiver ("No consent form" + why). Sent as
  // TypeSpecific.waivers.consent_form = { reason }; the server stamps by/at.
  ConsentWaived:                false,
  ConsentWaiverReason:          '',
  // Step 2 — NearMiss-only (not persisted as columns; packed into MedicalNotes on submit)
  NM_ReportingFunction:         '',
  NM_Category:                  '',
  NM_CategoryOther:             '',
  NM_Description:               '',
  NM_Cause:                     '',
  NM_CorrectiveAction:          '',
  NM_AssigneeRole:              '',
  NM_AssigneeOther:             '',
  NM_TargetResolutionDate:      '',
  NM_PotentialSeverityTier:     '',
  // Step 2 — Refusal Register (a Compliance near miss; refusal_register_logic.md).
  // The narrative reuses NM_Description ("Details of the incident") and the staff
  // member is the case's own StaffMemberConcerned — no duplicate fields.
  RR_RequestedAt:               '',   // 'YYYY-MM-DDTHH:mm' — sets the incident date
  RR_BuyerDescription:          '',
  RR_ActionTaken:               '',
  // Step 2 — ClinicIncident-only (incident_category + type_specific bag on submit)
  CI_IncidentType:              '',
  CI_IncidentTypeOther:         '',
  CI_PersonStatus:              '',
  CI_InjuredName:               '',
  CI_InjuredContact:            '',
  CI_Description:               '',
  CI_InjuryNature:              '',
  CI_ImmediateResponse:         '',
  CI_WitnessName:               '',
  CI_WitnessContact:            '',
  CI_WitnessStatement:          '',
};

// Filename helpers (pascalise, fileExt, categoryToken, previewFileName,
// buildUploadFile) now live in lib/attachmentNaming.js — shared with the
// CaseDetail slot UI. See top-of-file imports.

// Step validators (validateStep1 / validateStep2 / ENFORCE_REQUIRED) live in
// lib/wizardValidation.js — a pure module so `node --test` can exercise them.

export default function NewCaseWizard() {
  const navigate = useNavigate();
  const { user }   = useAuth();
  const { config } = useAppConfig();
  // Defensive — older AppConfig payloads may not carry treatmentTypes
  const treatmentTypes = config?.treatmentTypes || [];
  const severityCodes  = config?.severityCodes  || [];
  const eventTypes     = config?.eventTypes     || [];
  const categoryTree   = config?.incidentCategoriesByTreatmentAndCode;

  const [step,        setStep]        = useState(1);
  const [form,        setForm]        = useState(EMPTY_FORM);
  const [errors,      setErrors]      = useState({});
  const [submitting,  setSubmitting]  = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [createdId,   setCreatedId]   = useState(null);
  // A submit waiting on a PIN re-entry must not resend once the user has left
  // the wizard (Back / navigation) — the case would be created out of sight.
  const mountedRef = useRef(true);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  // A Refusal Register entry: a Compliance near miss filed under the register
  // category. It swaps the Step-2 body, closes itself on submission and carries
  // the locked #RefusalRegister tag (refusal_register_logic.md).
  const isRefusalRegister = form.EventType === 'NearMiss'
    && form.NM_Category === REFUSAL_REGISTER_CATEGORY;

  // Attachments — each entry: { file, category, otherLabel? }
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [uploadStatus,  setUploadStatus]  = useState([]);

  const [clinics,         setClinics]         = useState([]);
  const [tmData,          setTmData]          = useState(null);
  const [clinicFilter,    setClinicFilter]    = useState('GB');
  const [clinicLoadError, setClinicLoadError] = useState(null);
  const [clinicRetryNonce, setClinicRetryNonce] = useState(0);

  // Treatment-guide placement: the reference rail only fits beside the form on
  // wide screens; below the breakpoint it becomes a slide-over drawer opened
  // from a "Code reference" button in Step 2.
  const [railFits,  setRailFits]  = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1281px)').matches
  );
  const [guideOpen, setGuideOpen] = useState(false);
  const formPanelRef = useRef(null);
  const [guideHeight, setGuideHeight] = useState(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 1281px)');
    const onChange = e => setRailFits(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Close the drawer whenever its context goes away (step / treatment / layout)
  useEffect(() => { setGuideOpen(false); }, [step, form.Department, railFits]);

  useEffect(() => {
    if (!guideOpen) return;
    const onKey = e => { if (e.key === 'Escape') setGuideOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [guideOpen]);

  // Wide-screen rail: cap the reference panel to the form panel's height so long
  // guide content (full-ratio images) scrolls inside instead of stretching the row.
  useEffect(() => {
    if (!railFits || step !== 2) {
      setGuideHeight(null);
      return;
    }
    const el = formPanelRef.current;
    if (!el) return;
    const update = () => setGuideHeight(el.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [railFits, step, form.Department, form.Severity, form.TreatmentSubtype, form.EventType]);

  useEffect(() => {
    setClinicLoadError(null);
    getTherapieMapsData()
      .then(data => {
        setClinics(data.clinics || []);
        setTmData(data);
      })
      .catch(err => setClinicLoadError(err.message));
  }, [clinicRetryNonce]);

  const filteredClinics = clinics
    // Pending = not yet open in Maps: no clients, so nothing to report against.
    // Closed clinics stay selectable (historical cases) — Pending never had any.
    // CM assigned-clinic prefills are NOT filtered: a CM designated to a
    // pending clinic keeps their prefill rather than facing an empty picker.
    .filter(c => (c.clinicStatus || '').toLowerCase() !== 'pending')
    .filter(c => {
      const region  = (c.region  || '').toUpperCase();
      const country = (c.country || '').toLowerCase();
      if (clinicFilter === 'IOI') {
        return region.startsWith('IE') || region.startsWith('IOI') ||
               country.includes('ireland') || country === 'roi';
      }
      if (clinicFilter === 'US') {
        // Key US on country (region naming isn't fixed yet) so US clinics can't
        // fall through into the GB bucket the way an implicit else would.
        return country.includes('united states') || country === 'usa' ||
               country === 'us' || region.startsWith('US');
      }
      // GB — kept explicit (not a catch-all) so non-GB/non-IOI clinics don't leak in.
      return region.startsWith('GB') || country.includes('united kingdom') ||
             country.includes('england') || country.includes('scotland') ||
             country.includes('wales') || country.includes('northern ireland') ||
             country === 'uk' || country === 'gb';
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  const selectedClinic   = clinics.find(c => c.name === form.Clinic) || null;
  const assignedClinics  = getAssignedClinics(user, clinics, tmData);
  const cmClinicGroups   = assignedClinicGroups(assignedClinics);
  // HoRO files region-wide: the clinics loaded from getReferenceClinics are
  // already region-filtered server-side, so HoRO picks from that list and
  // Region/Country auto-populate from the chosen clinic. A single-clinic CM
  // stays read-only (their one clinic is prefilled).
  const isHoRO           = user?.role === 'HoRO';
  // A clinic-mailbox sub-identity reports only for its own clinic — locked and
  // prefilled, like a single-clinic CM.
  const isClinicMailbox  = user?.role === 'ClinicMailbox';
  const isClinicReadOnly = isSingleClinicManager(user, tmData) || isClinicMailbox;
  // For a mailbox sub-identity: Reporter is the registered STAFF MEMBER (the
  // signed-in person), and Role is the job title they registered with — not the
  // shared account name or the technical "ClinicMailbox".
  // (Assuming a mailbox identity has no sub-session, so the person being viewed
  //  as comes from the impersonation target — never the shared account email.)
  const reporterDisplay  = isClinicMailbox
    ? (getSubSessionName() || user?.subIdentityName || user?.name || '')
    : (user?.name || '');
  const roleDisplay      = isClinicMailbox
    ? (getSubSessionJobTitle() || 'Clinic staff')
    : (user?.roleLabel || user?.role || '');
  const isMultiClinicCM  = isMultiClinicManager(user, tmData);
  const cmSpansMultipleGroups = isMultiClinicCM && cmClinicGroups.spansMultiple;
  // Country toggle: all non-CM pickers, plus multi-site CMs assigned across
  // more than one country group (GB/IOI/US).
  const showClinicCountryToggle = (!isClinicReadOnly && !isMultiClinicCM && !isHoRO)
    || cmSpansMultipleGroups;
  const multiCmClinics = isMultiClinicCM
    ? (cmSpansMultipleGroups
      ? assignedClinics.filter(c => clinicMatchesCountryFilter(c, clinicFilter))
      : assignedClinics)
    : [];

  // Pre-fill (and lock) the clinic for a clinic-mailbox sub-identity from the
  // AUTHORITATIVE context (the same value the server scopes on), so it always
  // matches — a stale session clinic must not desync from the scope. Session is
  // only a first paint to avoid a flash.
  useEffect(() => {
    if (!isClinicMailbox) return;
    const fromSession = getSubSessionClinic();
    if (fromSession) setForm(prev => (prev.Clinic ? prev : { ...prev, Clinic: fromSession }));
    let cancelled = false;
    getClinicContext().then(c => {
      if (!cancelled && c?.clinicName) setForm(prev => ({ ...prev, Clinic: c.clinicName }));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [isClinicMailbox]);

  // Pre-fill clinic for CM — assignment from /reference/clinics is authoritative.
  useEffect(() => {
    if (user?.role !== 'ClinicManager') return;
    const assigned = getAssignedClinics(user, clinics, tmData);
    const name = assigned[0]?.name || defaultAssignedClinicName(user, tmData);
    if (!name) return;

    setForm(prev => (prev.Clinic ? prev : { ...prev, Clinic: name }));

    if (assigned.length) {
      const { groups } = assignedClinicGroups(assigned);
      // One group → select it; assigned across several → land on the first
      // assigned clinic's group (works for any of GB/IOI/US).
      if (groups.length === 1) setClinicFilter(groups[0]);
      else if (groups.length > 1) {
        const g = clinicCountryGroup(assigned[0]);
        if (g) setClinicFilter(g);
      }
    }
  }, [user?.role, user?.clinicObjects, clinics, tmData]);

  // Pre-fill ClinicManagerResponsible with the CM's own name
  useEffect(() => {
    if (user?.role !== 'ClinicManager') return;
    setForm(prev => prev.ClinicManagerResponsible
      ? prev
      : { ...prev, ClinicManagerResponsible: user.name || '' });
  }, [user?.role, user?.name]);

  // Auto-populate Region, Country, CM on clinic select
  useEffect(() => {
    if (!form.Clinic) return;
    const clinic = clinics.find(c => c.name === form.Clinic);
    if (!clinic) return;
    const updates = {
      Region:  clinic.region  || '',
      Country: clinic.country || '',
    };
    getTherapieMapsData()
      .then(tm => {
        const clinicEmail = (clinic.email || '').toLowerCase();
        const cmsForClinic = clinicEmail
          ? tm.cms.filter(cm => (cm.designatedClinic || []).some(e => e.toLowerCase() === clinicEmail))
          : [];
        // ALWAYS assign — a clinic with no CM in Maps must CLEAR the field, not
        // inherit the previously selected clinic's manager (see
        // resolveClinicManagerName).
        updates.ClinicManagerResponsible = resolveClinicManagerName(cmsForClinic, user);
        setForm(prev => ({ ...prev, ...updates }));
      })
      // Maps unavailable: whatever is in the field was resolved for a DIFFERENT
      // clinic, so it is stale by construction — clear it rather than keep it.
      .catch(() => setForm(prev => ({
        ...prev, ...updates, ClinicManagerResponsible: resolveClinicManagerName([], user),
      })));
  }, [form.Clinic, clinics]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset incident category when treatment / subtype / code changes
  useEffect(() => {
    setForm(prev => ({ ...prev, IncidentCategory: '', IncidentCategoryOther: '' }));
  }, [form.Department, form.TreatmentSubtype, form.Severity]);

  // Non-coded treatments (not LHR/AWI) have no severity — clear any leftover code
  // when such a treatment is selected so it can't be submitted with a stale code.
  useEffect(() => {
    if (form.Department && !treatmentHasCodes(form.Department) && form.Severity) {
      setForm(prev => ({ ...prev, Severity: '' }));
    }
  }, [form.Department]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset the subtype when the selected treatment doesn't define it (config-driven)
  useEffect(() => {
    if (!form.TreatmentSubtype) return;
    const allowed = subtypesForTreatment(treatmentTypes, form.Department, categoryTree, severityCodes);
    if (form.TreatmentSubtype === SUBTYPE_OTHER) {
      if (!allowed.length) setForm(prev => ({ ...prev, TreatmentSubtype: '', TreatmentSubtypeOther: '' }));
      return;
    }
    if (!allowed.includes(form.TreatmentSubtype)) {
      setForm(prev => ({ ...prev, TreatmentSubtype: '', TreatmentSubtypeOther: '' }));
    }
  }, [form.Department, treatmentTypes, categoryTree, severityCodes]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the event type changes, drop any treatment/severity selection that is
  // no longer available for it (unknown/legacy values are left intact).
  useEffect(() => {
    setForm(prev => {
      const tx  = (config?.treatmentTypes || []).find(t => t.value === prev.Department);
      const sev = (config?.severityCodes  || []).find(c => c.value === prev.Severity);
      const txOk  = !prev.Department || !tx  || itemAvailableForEventType(tx,  prev.EventType);
      const sevOk = !prev.Severity   || !sev || itemAvailableForEventType(sev, prev.EventType);
      if (txOk && sevOk) return prev;
      return { ...prev, Department: txOk ? prev.Department : '', Severity: sevOk ? prev.Severity : '' };
    });
  }, [form.EventType]); // eslint-disable-line react-hooks/exhaustive-deps

  // "Refusal Register" exists only under Compliance — switching the reporting
  // function away from it drops the selection (and its register fields) rather
  // than leaving a compliance register entry filed under Facilities.
  useEffect(() => {
    if (form.NM_Category !== REFUSAL_REGISTER_CATEGORY) return;
    if (form.NM_ReportingFunction === REFUSAL_REGISTER_FUNCTION) return;
    setForm(prev => ({
      ...prev,
      NM_Category: '', RR_RequestedAt: '', RR_BuyerDescription: '', RR_ActionTaken: '',
    }));
  }, [form.NM_ReportingFunction]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep EventType on an *enabled* type — e.g. if an admin disabled the default
  // ('Treatment'), fall back to the first enabled type so a disabled type can't
  // be submitted via the default.
  useEffect(() => {
    const enabled = (eventTypes || []).filter(t => t.enabled);
    if (enabled.length && !enabled.some(t => t.value === form.EventType)) {
      setForm(prev => ({ ...prev, EventType: enabled[0].value }));
    }
  }, [eventTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handlers ────────────────────────────────────────────────────────────
  function handleChange(e) {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: undefined }));
  }

  function handleTypeSpecificChange(fieldKey, value) {
    setForm(prev => ({
      ...prev,
      TypeSpecific: { ...prev.TypeSpecific, [fieldKey]: value },
    }));
  }

  // Each queued item: { file, category, otherLabel, metadata } — metadata is the
  // per-category detail from SlotUploadModal (post_photo.takenOn, …) or null.
  function addAttachment({ file, category, otherLabel, metadata }) {
    setAttachedFiles(prev => [...prev, { file, category, otherLabel: otherLabel || null, metadata: metadata || null }]);
    // Adding a real consent form (via any picker) visibly withdraws a prior
    // "No consent form" declaration — never silently, never both at once.
    // Removing the file again does NOT re-tick it.
    if (category === 'consent_form') {
      setForm(prev => (prev.ConsentWaived ? { ...prev, ConsentWaived: false } : prev));
      setErrors(prev => (prev.ConsentEvidence ? { ...prev, ConsentEvidence: undefined } : prev));
    }
  }

  function removeFile(i) {
    setAttachedFiles(prev => prev.filter((_, idx) => idx !== i));
  }

  function formatBytes(bytes) {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  function goNext() {
    const errs = validateStep1(form, { clinicUnavailable: !!clinicLoadError });
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      const first = Object.keys(errs)[0];
      document.getElementById(first)?.focus();
      return;
    }
    setErrors({});
    setStep(2);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }

  function goBack() {
    setStep(1);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    // Step 1's "Continue" submits the form too — on step 1, advance instead of
    // creating the case (prevents a stray submit from skipping event details).
    if (step === 1) { goNext(); return; }
    const subtypesNow = subtypesForTreatment(treatmentTypes, form.Department, categoryTree, severityCodes);
    // Mirror the option lists Step2 renders, so an admin-emptied dropdown is
    // not demanded of a user who has nothing to pick.
    const txOptionsNow  = filterByEventType(treatmentTypes, form.EventType, { includeValue: form.Department });
    const sevOptionsNow = filterByEventType(severityCodes, form.EventType, { includeValue: form.Severity }).filter(c => !c.reviewOnly);
    const hasConsentUpload = attachedFiles.some(a => a.category === 'consent_form');
    // Unique categories of the files queued for upload right after creation —
    // postCase cannot see uploads (they arrive afterwards), so the server's
    // consent gate reads this declaration instead.
    const declaredUploads  = [...new Set(attachedFiles.map(a => a.category).filter(Boolean))];
    const errs = validateStep2(form, subtypesNow.length > 0, {
      hasTreatmentOptions: txOptionsNow.length > 0,
      hasSeverityOptions:  sevOptionsNow.length > 0,
      hasConsentUpload,
      // Mirrors the Step 2 dropdown: the category is demanded only while the
      // resolved list has options (AWI subtype "Other" / an emptied cell → none).
      hasCategoryOptions:  categories.length > 0,
    });
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      const first = Object.keys(errs)[0];
      document.getElementById(first)?.focus();
      return;
    }
    // Assume identity: the whole wizard is walkable for view parity, but
    // finishing the submission as the assumed user is blocked (the API would
    // reject it anyway — this keeps the message friendly and nothing is sent).
    if (user?.impersonating) {
      setSubmitError(`You're viewing as ${user.email} — submitting a case is disabled while assuming an identity. Exit "Assume identity" to submit.`);
      return;
    }
    try {
      setSubmitting(true);
      setSubmitError(null);

      let payload;
      if (isRefusalRegister) {
        // Refusal Register — a Compliance near miss recording a refused booking.
        // Same columns as any near miss; the register's five items replace the
        // cause / corrective action / assignee set, and the exact moment of the
        // request sets the incident date. The server tags it, closes it and
        // re-validates every field (refusal_register_logic.md).
        payload = {
          Title:                    form.Title,
          Clinic:                   form.Clinic,
          Region:                   form.Region,
          Country:                  form.Country,
          DateofIncident:           refusalDateOf(form.RR_RequestedAt) || form.DateofIncident,
          EventType:                'NearMiss',
          Department:               null,
          ServiceCarriedOut:        null,
          Severity:                 null,
          IncidentCategory:         REFUSAL_REGISTER_CATEGORY,
          IncidentCategoryOther:    null,
          StaffMemberConcerned:     form.StaffMemberConcerned,
          ClinicManagerResponsible: form.ClinicManagerResponsible,
          IncidentDescription:      form.NM_Description,
          ClientPhoneNumber:        null,
          TypeSpecific: {
            reportingFunction:                      REFUSAL_REGISTER_FUNCTION,
            [REFUSAL_TS_KEYS.requestedAt]:      form.RR_RequestedAt,
            [REFUSAL_TS_KEYS.buyerDescription]: form.RR_BuyerDescription,
            [REFUSAL_TS_KEYS.actionTaken]:      form.RR_ActionTaken,
          },
        };
      } else if (form.EventType === 'NearMiss') {
        // Persist near-miss fields into real columns (migration 007):
        // event_type discriminator, category → incident_category, the narrative
        // → incident_description (immutable, migration 016), rest → type_specific.
        const assignee = form.NM_AssigneeRole === 'Other'
          ? form.NM_AssigneeOther
          : (NEAR_MISS_ASSIGNEES.find(a => a.value === form.NM_AssigneeRole)?.label || form.NM_AssigneeRole);
        const potentialSeverity =
          NEAR_MISS_SEVERITY_TIERS.find(t => t.value === form.NM_PotentialSeverityTier)?.label || null;

        payload = {
          Title:                    form.Title,
          Clinic:                   form.Clinic,
          Region:                   form.Region,
          Country:                  form.Country,
          DateofIncident:           form.DateofIncident,
          EventType:                'NearMiss',
          Department:               null,
          ServiceCarriedOut:        null,
          Severity:                 null,
          IncidentCategory:         form.NM_Category,
          IncidentCategoryOther:    form.NM_Category === 'Other' ? form.NM_CategoryOther : null,
          StaffMemberConcerned:     form.StaffMemberConcerned,
          ClinicManagerResponsible: form.ClinicManagerResponsible,
          IncidentDescription:      form.NM_Description,
          ClientPhoneNumber:        null,
          TypeSpecific: {
            reportingFunction:    form.NM_ReportingFunction,
            cause:                form.NM_Cause,
            correctiveAction:     form.NM_CorrectiveAction,
            assignee,
            targetResolutionDate: form.NM_TargetResolutionDate,
            potentialSeverity,
          },
        };
      } else if (form.EventType === 'ClinicIncident') {
        // Clinic incident — like Near Miss, no treatment cascade or severity.
        // Incident type → incident_category; the account → incident_description
        // (immutable, migration 016); injured-person / witness → type_specific.
        // Spec: clinic_incident_logic.md.
        payload = {
          Title:                    form.Title,
          Clinic:                   form.Clinic,
          Region:                   form.Region,
          Country:                  form.Country,
          DateofIncident:           form.DateofIncident,
          EventType:                'ClinicIncident',
          Department:               null,
          ServiceCarriedOut:        null,
          Severity:                 null,
          IncidentCategory:         form.CI_IncidentType,
          IncidentCategoryOther:    form.CI_IncidentType === 'Other' ? form.CI_IncidentTypeOther : null,
          StaffMemberConcerned:     form.StaffMemberConcerned,
          ClinicManagerResponsible: form.ClinicManagerResponsible,
          IncidentDescription:      form.CI_Description,
          ClientPhoneNumber:        null,
          TypeSpecific: {
            personStatus:      form.CI_PersonStatus,
            injuredName:       form.CI_InjuredName      || null,
            injuredContact:    form.CI_InjuredContact   || null,
            injuryNature:      form.CI_InjuryNature     || null,
            immediateResponse: form.CI_ImmediateResponse || null,
            witnessName:       form.CI_WitnessName      || null,
            witnessContact:    form.CI_WitnessContact   || null,
            witnessStatement:  form.CI_WitnessStatement || null,
          },
        };
      } else {
        // Treatment event — persist the cascade fields + type_specific extras.
        payload = {
          Title:                        form.Title,
          Clinic:                       form.Clinic,
          Region:                       form.Region,
          Country:                      form.Country,
          DateofIncident:               form.DateofIncident,
          EventType:                    form.EventType,
          Department:                   form.Department,
          TreatmentSubtype:             resolveTreatmentSubtypeValue(form.TreatmentSubtype, form.TreatmentSubtypeOther),
          IncidentCategory:             form.IncidentCategory || null,
          IncidentCategoryOther:        form.IncidentCategory === 'Other' ? form.IncidentCategoryOther : null,
          ServiceCarriedOut:            form.ServiceCarriedOut,
          Severity:                     treatmentHasCodes(form.Department) ? form.Severity : null,
          StaffMemberConcerned:         form.StaffMemberConcerned,
          ClinicManagerResponsible:     form.ClinicManagerResponsible,
          IncidentDescription:          form.MedicalNotes,
          ClientPhoneNumber:            form.ClientPhoneNumber,
          // Client review date — only meaningful (and only asked) for coded
          // treatments at Code 3+; never send a stale pick from a higher code.
          ReviewDate:                   reviewDateRequired(form.Department, form.Severity) ? (form.ReviewDate || null) : null,
          DeclaredUploads:              declaredUploads,
          TypeSpecific: (() => {
            // Submit-evidence hygiene: laser settings are trimmed (dropped when
            // blank) and kept only while some current selection asks for them
            // (the C1–2 evidence panel or the Code 5 matrix); typed settings win
            // over a stale "none" declaration; the flag is stored only when
            // true and never survives outside LHR Code 1–2. The UI already
            // prevents a "none" flag coexisting with its real counterpart, so a
            // kept flag is always a deliberate declaration.
            const ts = { ...(form.TypeSpecific || {}) };
            if (typeof ts.laser_settings === 'string') {
              const trimmed = ts.laser_settings.trim();
              if (trimmed) ts.laser_settings = trimmed; else delete ts.laser_settings;
            }
            const evidence = submitEvidenceRequired(form.Department, form.Severity);
            const matrixFields = getRequirements(form.Department, treatmentHasCodes(form.Department) ? form.Severity : null)?.fields || [];
            if (!evidence && !matrixFields.includes('laser_settings')) delete ts.laser_settings;
            if (!evidence || ts.no_laser_settings !== true || ts.laser_settings) delete ts.no_laser_settings;
            // Blank text and an unanswered yes/no (null) carry nothing — drop
            // them so optional AWI fields left empty never appear as "" rows.
            for (const k of Object.keys(ts)) {
              if (ts[k] == null || (typeof ts[k] === 'string' && !ts[k].trim())) delete ts[k];
            }
            // The consent declaration is a WAIVER (type_specific.waivers), never
            // the legacy PR #65 flag. Sent only while no consent file is queued;
            // the server stamps byName/byEmail/at from the caller.
            delete ts.no_consent_form;
            delete ts.waivers;
            if (form.ConsentWaived && !hasConsentUpload) {
              ts.waivers = { consent_form: { reason: form.ConsentWaiverReason.trim() } };
            }
            return Object.keys(ts).length ? ts : null;
          })(),
        };
      }
      let result;
      try {
        result = await postCase(payload);
      } catch (e) {
        if (e.code !== SUB_SESSION_EXPIRED) throw e;
        // The clinic PIN session lapsed while the form was open: the PIN pad is
        // up over this page (the form stays as typed) — send again once it's in.
        await waitForSubSessionRenewal();
        if (!mountedRef.current) return;
        result = await postCase(payload);
      }

      if (attachedFiles.length > 0) {
        const statuses = [];
        const used = new Map(); // categoryToken → count, used to disambiguate duplicates
        for (const item of attachedFiles) {
          const token = categoryToken(item);
          const seen  = used.get(token) || 0;
          used.set(token, seen + 1);
          const renamed = buildUploadFile(item, result.id, seen);
          try {
            // Category + metadata travel with EVERY upload so the slot binding
            // (attachments.category) and the per-file detail (attachments.metadata)
            // are stored, not inferred from the filename later.
            await uploadCaseAttachment(result.id, renamed, { category: item.category, metadata: item.metadata });
            statuses.push({ name: renamed.name, ok: true });
          } catch (uploadErr) {
            statuses.push({ name: renamed.name, ok: false, error: uploadErr.message });
          }
        }
        setUploadStatus(statuses);
      }

      window.scrollTo({ top: 0, behavior: 'instant' });
      setCreatedId(result.id);
    } catch (err) {
      setSubmitError(err.message || 'Failed to create case.');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Derived: requirements + categories for the current selection ───────
  // Config-driven subtypes for the selected treatment. Replaces the old
  // hardcoded AWI_SUBTYPES / `Department === 'AWI'` checks — adding a treatment
  // with a `subtypes` field surfaces a subtype picker with no code change.
  const subtypeOptions  = subtypesForTreatment(treatmentTypes, form.Department, categoryTree, severityCodes);
  const hasSubtypes     = subtypeOptions.length > 0;
  const requirements    = getRequirements(form.Department, form.Severity);
  // Non-coded treatments have no severity; their conditions are identical under
  // every code (flatAcrossCodes), so read categories with a sentinel code so the
  // list still shows without a severity selection.
  const categoryCode    = treatmentHasCodes(form.Department) ? form.Severity : 'Code 1';
  const effectiveSubtype  = resolveTreatmentSubtypeValue(form.TreatmentSubtype, form.TreatmentSubtypeOther);
  const categories      = getIncidentCategoriesForUser(config, form.Department, effectiveSubtype, categoryCode, {
    canUseFullLhrCategories: !!user?.canManageRequirements,
  });
  // Nobody is asked for HQ-held items when filing — whatever their role: the
  // training certificate (and the training date / trainer name that now travel
  // with the certificate file) is uploaded by the training team on the case
  // file afterwards. Laser log files STAY on submission for now (§7).
  const extraFieldKeys    = (requirements?.fields         || []).filter(k => !HQ_ONLY_REQUIREMENT_FIELDS.has(k));
  const optionalFieldKeys = (requirements?.optionalFields || []).filter(k => !HQ_ONLY_REQUIREMENT_FIELDS.has(k));
  const submitRequiredSet = new Set(submitRequiredFields(form.Department));
  const requiredUploads   = (requirements?.uploads        || []).filter(k => !HQ_ONLY_REQUIREMENT_UPLOADS.has(k));
  // Every Treatment submission captures the consent form (upload or waiver), so
  // it is a submission-required category even when the matrix row lacks it.
  const submitRequiredUploads = form.EventType === 'Treatment'
    ? [...new Set(['consent_form', ...requiredUploads])]
    : requiredUploads;

  // Attachment dropdown options — required categories first, then the rest
  const attachmentOptions = (() => {
    const ordered = [];
    for (const k of submitRequiredUploads) {
      const cat = ATTACHMENT_CATEGORIES.find(c => c.key === k);
      if (cat) ordered.push(cat);
    }
    for (const c of ATTACHMENT_CATEGORIES) {
      if (!ordered.find(o => o.key === c.key)) ordered.push(c);
    }
    return ordered;
  })();

  // ── Success state ──────────────────────────────────────────────────────
  if (createdId) {
    return (
      <>
        <header className="page-head">
          <div>
            <div className="breadcrumb">
              <a href="#" onClick={e => { e.preventDefault(); navigate('/dashboard'); }}>Overview</a>
              <span>›</span>
              <span>New submission</span>
            </div>
            <h1>Case submitted</h1>
          </div>
        </header>
        <div className="callout brand" style={{ marginBottom: '20px' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          <div><strong>Case #{createdId} created.</strong> It is now available in the All Cases list.</div>
        </div>

        {uploadStatus.length > 0 && (
          <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {uploadStatus.map((s, i) => (
              <div key={i} className={`callout ${s.ok ? 'brand' : 'danger'}`} style={{ padding: '8px 12px' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14, flexShrink: 0 }}>
                  {s.ok
                    ? <polyline points="20 6 9 17 4 12"/>
                    : <><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></>}
                </svg>
                <div style={{ fontSize: 13 }}>
                  {s.ok ? <><strong>{s.name}</strong> uploaded</> : <><strong>{s.name}</strong> failed — {s.error}</>}
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn primary" onClick={() => navigate(`/cases/${createdId}`)}>View Case #{createdId}</button>
          <button className="btn" onClick={() => {
            setForm(EMPTY_FORM); setCreatedId(null); setErrors({});
            setAttachedFiles([]); setUploadStatus([]); setStep(1);
          }}>Report another</button>
          <button className="btn ghost" onClick={() => navigate('/cases')}>All cases</button>
        </div>
      </>
    );
  }

  // ── Wizard chrome ──────────────────────────────────────────────────────
  // Show the treatment reference rail on Step 2 once a guided treatment (e.g.
  // LHR) is selected. Purely an identification aid — see TreatmentGuide.jsx.
  const showGuide = step === 2
    && form.EventType === 'Treatment'
    && !!getTreatmentGuide(form.Department);

  return (
    <>
      <header className="page-head" style={{ maxWidth: 860 }}>
        <div>
          <div className="breadcrumb">
            <a href="#" onClick={e => { e.preventDefault(); navigate('/dashboard'); }}>Overview</a>
            <span>›</span>
            <span>New submission</span>
          </div>
          <h1>New case submission</h1>
          <div className="page-sub">Report an adverse event, near miss, or clinic incident — pick the event type, then fill in the details.</div>
        </div>
        <div className="head-actions">
          <button className="btn" type="button" onClick={() => navigate('/dashboard')}>Cancel</button>
        </div>
      </header>

      {/* Submit error */}
      {submitError && (
        <div className="callout danger" style={{ marginBottom: 16 }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <div><strong>Submission failed</strong> — {submitError}</div>
        </div>
      )}

      {/* Step indicator */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, maxWidth: 860 }}>
        <StepPill n={1} label="General info"    active={step === 1} done={step > 1} onClick={() => setStep(1)} />
        <StepPill n={2} label="Event details" active={step === 2} done={false}    onClick={() => null /* gated via Continue */} />
      </div>

      <form onSubmit={handleSubmit} noValidate>
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div className="panel" ref={formPanelRef} style={{ flex: '1 1 620px', maxWidth: 860, minWidth: 0 }}>
          <div className="panel-body">
            {step === 1 ? (
              <Step1
                form={form} errors={errors} user={user}
                reporterDisplay={reporterDisplay} roleDisplay={roleDisplay}
                eventTypes={eventTypes}
                isClinicReadOnly={isClinicReadOnly}
                isMultiClinicCM={isMultiClinicCM}
                isHoRO={isHoRO}
                showClinicCountryToggle={showClinicCountryToggle}
                multiCmClinics={multiCmClinics}
                assignedClinics={assignedClinics}
                clinics={clinics} clinicLoadError={clinicLoadError}
                onRetryClinics={() => setClinicRetryNonce(n => n + 1)}
                clinicFilter={clinicFilter} setClinicFilter={setClinicFilter}
                filteredClinics={filteredClinics} selectedClinic={selectedClinic}
                tmData={tmData}
                setForm={setForm} setErrors={setErrors} handleChange={handleChange}
              />
            ) : (
              <Step2
                form={form} errors={errors}
                eventTypes={eventTypes}
                treatmentTypes={filterByEventType(treatmentTypes, form.EventType, { includeValue: form.Department })}
                severityCodes={filterByEventType(severityCodes, form.EventType, { includeValue: form.Severity }).filter(c => !c.reviewOnly)}
                subtypeOptions={subtypeOptions}
                hasSubtypes={hasSubtypes}
                categories={categories}
                requirements={requirements}
                extraFieldKeys={extraFieldKeys}
                optionalFieldKeys={optionalFieldKeys}
                submitRequiredSet={submitRequiredSet}
                requiredUploads={requiredUploads}
                submitRequiredUploads={submitRequiredUploads}
                attachmentOptions={attachmentOptions}
                attachedFiles={attachedFiles}
                handleChange={handleChange}
                handleTypeSpecificChange={handleTypeSpecificChange}
                addAttachment={addAttachment}
                removeFile={removeFile}
                formatBytes={formatBytes}
                setForm={setForm} setErrors={setErrors}
                onOpenGuide={showGuide && !railFits ? () => setGuideOpen(true) : null}
              />
            )}

            {/* ── Step nav ── */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 16, marginTop: 16, borderTop: '1px solid var(--border)' }}>
              {step === 1 ? (
                <button className="btn" type="button" onClick={() => navigate('/dashboard')}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                  Cancel
                </button>
              ) : (
                <button className="btn" type="button" onClick={goBack}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                  Back
                </button>
              )}
              {step === 1 ? (
                <button className="btn primary" type="submit">
                  Continue to event details
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                </button>
              ) : (
                <button className="btn primary" type="submit" disabled={submitting}>
                  {submitting
                    ? (attachedFiles.length > 0 ? 'Submitting & uploading…' : 'Submitting…')
                    : `Submit case${attachedFiles.length > 0 ? ` + ${attachedFiles.length} file${attachedFiles.length > 1 ? 's' : ''}` : ''}`}
                  {!submitting && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>}
                </button>
              )}
            </div>
          </div>
        </div>

        {showGuide && railFits && (
          <div style={{
            flex: '1 1 320px', maxWidth: 380, minWidth: 260,
            height: guideHeight ?? undefined,
            display: 'flex', flexDirection: 'column', minHeight: 0,
          }}>
            <TreatmentGuide variant="rail" treatment={form.Department} activeCode={form.Severity} />
          </div>
        )}
        </div>
      </form>

      {/* Narrow-screen guide drawer — same content as the rail, slid over the form */}
      {showGuide && !railFits && guideOpen && (
        <div role="dialog" aria-modal="true" aria-label="Code reference" style={{ position: 'fixed', inset: 0, zIndex: 300 }}>
          <div
            onClick={() => setGuideOpen(false)}
            style={{ position: 'absolute', inset: 0, background: 'rgba(15, 23, 42, .45)' }}
          />
          <div style={{
            position: 'absolute', top: 0, right: 0, bottom: 0,
            width: 'min(400px, 92vw)',
            background: 'var(--surface)',
            boxShadow: '-12px 0 32px rgba(0, 0, 0, .22)',
            display: 'flex', flexDirection: 'column',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 8, padding: '10px 12px 10px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0,
            }}>
              <span style={{ fontSize: 13, fontWeight: 700 }}>Code reference</span>
              <button className="btn" type="button" aria-label="Close code reference" onClick={() => setGuideOpen(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            {/* Definite-height flex child so the guide's maxHeight:100% makes it the scroller */}
            <div style={{ flex: 1, minHeight: 0, padding: 12, display: 'flex', flexDirection: 'column' }}>
              <TreatmentGuide variant="drawer" treatment={form.Department} activeCode={form.Severity} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Step pill ────────────────────────────────────────────────────────────
function StepPill({ n, label, active, done, onClick }) {
  return (
    <button type="button" onClick={onClick} disabled={!active && !done} style={{
      display: 'flex', alignItems: 'center', gap: 10,
      flex: 1, padding: '10px 14px',
      background: active ? 'var(--surface)' : (done ? 'var(--surface-2)' : 'var(--surface-2)'),
      border: '1px solid', borderColor: active ? 'var(--brand)' : 'var(--border)',
      borderRadius: 10, cursor: (active || done) ? 'pointer' : 'default',
      transition: 'all 200ms var(--ease)',
    }}>
      <span style={{
        display: 'grid', placeItems: 'center',
        width: 24, height: 24, borderRadius: '50%',
        background: active ? 'var(--brand)' : (done ? 'var(--ok)' : 'var(--surface-3)'),
        color: (active || done) ? '#fff' : 'var(--text-3)',
        fontWeight: 600, fontSize: 12, flexShrink: 0,
      }}>
        {done ? '✓' : n}
      </span>
      <span style={{
        fontSize: 13, fontWeight: 600,
        color: active ? 'var(--text)' : 'var(--text-2)',
      }}>
        Step {n} · {label}
      </span>
    </button>
  );
}

// ── Section header ───────────────────────────────────────────────────────
function SectionHead({ n, title, sub }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <h2 style={{ display: 'inline', verticalAlign: 'middle', fontSize: 16 }}>
        <span style={{
          display: 'inline-grid', placeItems: 'center',
          width: 24, height: 24, borderRadius: '50%',
          background: 'var(--brand-weak)', color: 'var(--brand-ink)',
          fontWeight: 600, fontSize: 12, marginRight: 10, verticalAlign: 'middle',
        }}>{n}</span>
        {title}
      </h2>
      {sub && (
        <p style={{ color: 'var(--text-2)', margin: '6px 0 0 34px', fontSize: 13 }}>{sub}</p>
      )}
    </div>
  );
}

// ── Step 1 ──────────────────────────────────────────────────────────────
function Step1({
  form, errors, user, reporterDisplay, roleDisplay, eventTypes, isClinicReadOnly, isMultiClinicCM, isHoRO,
  showClinicCountryToggle, multiCmClinics, assignedClinics,
  clinics, clinicLoadError, onRetryClinics, clinicFilter, setClinicFilter,
  filteredClinics, selectedClinic, tmData,
  setForm, setErrors, handleChange,
}) {
  // Near Miss and Clinic Incident share the non-treatment Step-1 layout:
  // an incident title (not a client), no client phone, staff optional.
  const isNonTreatment = form.EventType === 'NearMiss' || form.EventType === 'ClinicIncident';
  return (
    <>
      {/* Event type — gates the rest of the wizard */}
      <div style={{ marginBottom: 24, paddingBottom: 24, borderBottom: '1px solid var(--border)' }}>
        <SectionHead n={1} title="Event type" sub="What kind of event is this? Each type has its own form, severity scale, and required documentation." />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
          {[...eventTypes].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map(t => (
            <button key={t.value} type="button"
              onClick={() => {
                if (!t.enabled) return;
                setForm(prev => ({ ...prev, EventType: t.value }));
                // Each event type validates its own field set — errors from the
                // previous type (and their baked wording) don't apply to it.
                setErrors({});
              }}
              disabled={!t.enabled}
              style={{
                textAlign: 'left', padding: '12px 14px', borderRadius: 10,
                border: '1px solid', borderColor: form.EventType === t.value ? 'var(--brand)' : 'var(--border)',
                background: form.EventType === t.value ? 'var(--brand-weak)' : 'var(--surface)',
                color: t.enabled ? 'var(--text)' : 'var(--text-3)',
                cursor: t.enabled ? 'pointer' : 'not-allowed',
                opacity: t.enabled ? 1 : 0.6,
                transition: 'all 200ms var(--ease)',
              }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{t.label}</div>
              <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{t.hint}</div>
            </button>
          ))}
        </div>
        {errors.EventType && <div className="field-error" style={{ marginTop: 6 }}>{errors.EventType}</div>}
      </div>

      {/* Reporter & clinic */}
      <div style={{ marginBottom: 24, paddingBottom: 24, borderBottom: '1px solid var(--border)' }}>
        <SectionHead n={2} title="Reporter & clinic" sub="Who is reporting and where it happened." />
        <div className="field-row" style={{ alignItems: 'stretch' }}>
          {/* col1 — reporter + role; hint pinned to the bottom */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="field">
              <label>Reporter</label>
              {/* A clinic mailbox reports on behalf of the clinic; an ordinary
                  user reports as themselves. */}
              <input className="input" value={reporterDisplay} readOnly style={{ background: 'var(--surface-2)' }} />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label>Role</label>
              {/* For a mailbox sub-identity, the job title the person registered
                  with — not the technical ClinicMailbox role. */}
              <input className="input" value={roleDisplay} readOnly style={{ background: 'var(--surface-2)' }} />
            </div>
            <div className="field-hint" style={{ marginTop: 'auto', paddingTop: 8 }}>Automatically set from your profile</div>
          </div>

          {/* col2 — clinic; GB/IOI/US toggle sits on the label line; hint pinned to the bottom */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="field" style={{ marginBottom: 0 }}>
              <div style={{ position: 'relative', marginBottom: 6 }}>
                <label htmlFor="Clinic" style={{ marginBottom: 0 }}>Clinic <span style={{ color: 'var(--danger)' }}>*</span></label>
                {!showClinicCountryToggle ? null : (
                  <div style={{ position: 'absolute', right: 0, bottom: 0, display: 'flex', gap: 4 }}>
                    {['GB', 'IOI', 'US'].map(code => (
                      <button key={code} type="button"
                        onClick={() => { setClinicFilter(code); setForm(prev => ({ ...prev, Clinic: '' })); }}
                        style={{
                          padding: '3px 14px', fontSize: 12, fontWeight: 600,
                          borderRadius: 6, border: '1px solid',
                          borderColor: clinicFilter === code ? 'var(--brand)' : 'var(--border)',
                          cursor: 'pointer',
                          background: clinicFilter === code ? 'var(--brand)' : 'var(--surface)',
                          color:      clinicFilter === code ? '#fff' : 'var(--text-3)',
                          transition: 'background .15s, color .15s, border-color .15s',
                        }}>{code}</button>
                    ))}
                  </div>
                )}
              </div>

              {isClinicReadOnly ? (
                <input className="input"
                  value={form.Clinic || defaultAssignedClinicName(user, tmData) || (clinics.length === 0 && !tmData ? 'Loading…' : '')}
                  readOnly style={{ background: 'var(--surface-2)' }} />
              ) : isMultiClinicCM ? (
                <SearchableSelect
                  id="Clinic" name="Clinic"
                  value={form.Clinic} onChange={handleChange}
                  placeholder={clinics.length === 0 ? 'Loading clinics…' : 'Select clinic…'}
                  disabled={clinics.length === 0}
                  options={multiCmClinics.map(c => ({
                    value: c.name,
                    label: c.name.replace(/^Therapie Clinic\s*/i, ''),
                    sub:   [c.region, c.country].filter(Boolean).join(' · '),
                    meta:  clinicMeta(c),
                  }))}
                  noOptionsLabel="No assigned clinics found"
                />
              ) : isHoRO ? (
                // Region-scoped: `clinics` is already filtered to the HoRO's
                // region server-side. Picking one auto-populates Region/Country.
                <SearchableSelect
                  id="Clinic" name="Clinic"
                  value={form.Clinic} onChange={handleChange}
                  placeholder={clinics.length === 0 ? 'Loading clinics…' : 'Select a clinic in your region…'}
                  disabled={clinics.length === 0}
                  options={clinics.map(c => ({
                    value: c.name,
                    label: c.name.replace(/^Therapie Clinic\s*/i, ''),
                    sub:   [c.region, c.country].filter(Boolean).join(' · '),
                    meta:  clinicMeta(c),
                  }))}
                  noOptionsLabel="No clinics found in your region"
                />
              ) : clinicLoadError ? (
                <div className="callout danger" style={{ margin: '4px 0' }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  <div>
                    <strong>Could not load clinic list</strong> — {clinicLoadError}
                    <div style={{ marginTop: 8 }}>
                      <button className="btn" type="button" onClick={onRetryClinics}>Try again</button>
                    </div>
                  </div>
                </div>
              ) : (
                <SearchableSelect
                  id="Clinic" name="Clinic"
                  value={form.Clinic} onChange={handleChange}
                  placeholder={clinics.length === 0 ? 'Loading clinics…' : 'Select clinic…'}
                  disabled={clinics.length === 0}
                  options={filteredClinics.map(c => ({
                    value: c.name,
                    label: c.name.replace(/^Therapie Clinic\s*/i, ''),
                    sub:   [c.region, c.country].filter(Boolean).join(' · '),
                    meta:  clinicMeta(c),
                  }))}
                  noOptionsLabel={`No clinics match in ${clinicFilter}`}
                />
              )}

              {isClinicReadOnly
                ? ((form.Region || form.Country) && (
                    <div style={{ marginTop: 5, fontSize: 12, color: 'var(--text-3)' }}>{[form.Region, form.Country].filter(Boolean).join(' · ')}</div>
                  ))
                : (selectedClinic && (
                    <div style={{ marginTop: 5, fontSize: 12, color: 'var(--text-3)' }}>{[form.Region, form.Country, selectedClinic.address].filter(Boolean).join(' · ')}</div>
                  ))}
              {errors.Clinic && <div className="field-error">{errors.Clinic}</div>}
            </div>
            {(isClinicReadOnly || isMultiClinicCM || isHoRO) && (
              <div className="field-hint" style={{ marginTop: 'auto', paddingTop: 8 }}>
                {isClinicReadOnly ? 'Automatically set from your clinic assignment'
                  : isHoRO ? 'Choose the clinic in your region where this occurred'
                  : 'Choose which of your assigned clinics this occurred at'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Client information (3) + When it happened (4) — side by side */}
      <div style={{ marginBottom: 24, paddingBottom: 24, borderBottom: '1px solid var(--border)' }}>
        <div className="field-row">
          {/* col1 — Client information */}
          <div>
            <SectionHead n={3} title={isNonTreatment ? 'Incident title' : 'Client information'} />
            <div className="field">
              <label htmlFor="Title">{isNonTreatment ? 'Incident title' : 'Client name'} <span style={{ color: 'var(--danger)' }}>*</span></label>
              <CharCountInput fieldKey="Title" id="Title" name="Title" type="text"
                value={form.Title} onChange={handleChange}
                placeholder={isNonTreatment ? 'Short summary — e.g. "Slip on wet floor, reception"' : 'Full name'}
                style={errors.Title ? { borderColor: 'var(--danger)', boxShadow: '0 0 0 3px rgba(220,38,38,.12)' } : {}}
              />
              {errors.Title && <div className="field-error">{errors.Title}</div>}
            </div>
            {!isNonTreatment && (
              <div className="field" style={{ marginBottom: 0 }}>
                <label htmlFor="ClientPhoneNumber">Phone number <span style={{ color: 'var(--danger)' }}>*</span></label>
                <CharCountInput fieldKey="ClientPhoneNumber" id="ClientPhoneNumber" name="ClientPhoneNumber" type="tel"
                  value={form.ClientPhoneNumber} onChange={handleChange}
                  placeholder="e.g. +44 7700 900000"
                  style={errors.ClientPhoneNumber ? { borderColor: 'var(--danger)', boxShadow: '0 0 0 3px rgba(220,38,38,.12)' } : {}} />
                {errors.ClientPhoneNumber && <div className="field-error">{errors.ClientPhoneNumber}</div>}
              </div>
            )}
          </div>

          {/* col2 — When it happened */}
          <div>
            <SectionHead n={4} title="When it happened" />
            <div className="field" style={{ marginBottom: 0 }}>
              <label htmlFor="DateofIncident">Date of incident <span style={{ color: 'var(--danger)' }}>*</span></label>
              <DateInput id="DateofIncident" name="DateofIncident"
                value={form.DateofIncident} onChange={handleChange}
                max={todayIso()}
                style={errors.DateofIncident ? { borderColor: 'var(--danger)', boxShadow: '0 0 0 3px rgba(220,38,38,.12)' } : {}}
              />
              {errors.DateofIncident && <div className="field-error">{errors.DateofIncident}</div>}
            </div>
          </div>
        </div>
      </div>

      {/* Staff */}
      <div style={{ marginBottom: 8 }}>
        <SectionHead n={5} title={isNonTreatment ? 'Staff (optional)' : 'Staff involved'} />
        <div className="field-row">
          <div className="field">
            <label htmlFor="StaffMemberConcerned">
              {isNonTreatment ? 'Staff member involved (if any)'
                : <>Staff member concerned <span style={{ color: 'var(--danger)' }}>*</span></>}
            </label>
            <CharCountInput fieldKey="StaffMemberConcerned" id="StaffMemberConcerned" name="StaffMemberConcerned" type="text"
              value={form.StaffMemberConcerned} onChange={handleChange}
              placeholder={isNonTreatment ? 'Full name' : 'Full name — or N/A'}
              style={errors.StaffMemberConcerned ? { borderColor: 'var(--danger)', boxShadow: '0 0 0 3px rgba(220,38,38,.12)' } : {}} />
            {errors.StaffMemberConcerned && <div className="field-error">{errors.StaffMemberConcerned}</div>}
          </div>
          <div className="field">
            <label htmlFor="ClinicManagerResponsible">Clinic manager responsible</label>
            <CharCountInput fieldKey="ClinicManagerResponsible" id="ClinicManagerResponsible" name="ClinicManagerResponsible" type="text"
              value={form.ClinicManagerResponsible} onChange={handleChange}
              placeholder="Full name"
              readOnly={user?.role === 'ClinicManager'}
              style={user?.role === 'ClinicManager' ? { background: 'var(--surface-2)' } : undefined}
            />
            {form.ClinicManagerResponsible && form.Clinic && (
              <div className="field-hint">
                {user?.role === 'ClinicManager' ? 'Set from your account' : 'Auto-filled from clinic — edit if needed'}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ── Step 2 ──────────────────────────────────────────────────────────────
const SECTION_STYLE = { marginBottom: 24, paddingBottom: 24, borderBottom: '1px solid var(--border)' };
const BLOCK_LABEL   = { fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-3)', marginBottom: 8 };
const ERR_STYLE     = { borderColor: 'var(--danger)', boxShadow: '0 0 0 3px rgba(220,38,38,.12)' };

function Step2({
  form, errors, eventTypes, treatmentTypes, severityCodes,
  subtypeOptions, hasSubtypes,
  categories, requirements, extraFieldKeys, optionalFieldKeys = [], submitRequiredSet = new Set(), requiredUploads, submitRequiredUploads,
  attachmentOptions, attachedFiles,
  handleChange, handleTypeSpecificChange,
  addAttachment, removeFile, formatBytes,
  setForm, setErrors,
  onOpenGuide,
}) {
  const isTreatment      = form.EventType === 'Treatment';
  const isNearMiss       = form.EventType === 'NearMiss';
  const isClinicIncident = form.EventType === 'ClinicIncident';
  // Coded treatments (LHR/AWI) gate the category on a chosen severity; non-coded
  // treatments have no severity, so their category list shows as soon as there are
  // options for the selected treatment.
  const showCategoryDropdown = categories.length > 0 && (treatmentHasCodes(form.Department) ? !!form.Severity : true);
  const showSubtype          = hasSubtypes;
  // Review date — coded treatments at Code 3+ (required_evidence_logic.md §5).
  const showReviewDate       = isTreatment && reviewDateRequired(form.Department, form.Severity);
  // LHR Code 1–2: laser settings are captured at submission — the real thing or
  // an explicit "none recorded" declaration.
  const laserEvidence    = isTreatment && submitEvidenceRequired(form.Department, form.Severity);
  const laserOk          = !!String(form.TypeSpecific.laser_settings || '').trim() || form.TypeSpecific.no_laser_settings === true;
  // Consent form — EVERY Treatment submission: upload it, or switch on "No
  // consent form" and record why (required_evidence_logic.md §3).
  const hasConsentUpload = attachedFiles.some(a => a.category === 'consent_form');
  const consentOk        = hasConsentUpload || (form.ConsentWaived && !!String(form.ConsentWaiverReason || '').trim());
  // Requirements panel — the matrix row minus the consent form (it has its own
  // block above). Shown when there is anything required OR optional to offer.
  // An optional-only row (AWI Code 1–2) gets a neutral heading, no priority
  // callout and no "Documents" section, so nothing reads as required when it
  // is not.
  const panelUploads = requiredUploads.filter(k => k !== 'consent_form');
  const hasRequired  = extraFieldKeys.length > 0 || panelUploads.length > 0;
  const hasOptional  = optionalFieldKeys.length > 0;
  const showReqPanel = isTreatment && (hasRequired || hasOptional);
  // Section numbering — Treatment: Treatment & severity · Required to submit ·
  // [Required for this code / Additional details] · Incident description ·
  // Additional attachments. Near miss / clinic incident: their own section + attachments.
  let n = 1;
  const nTreat  = n++;
  const nSubmit = isTreatment ? n++ : null;
  const nReq    = showReqPanel ? n++ : null;
  const nDesc   = isTreatment ? n++ : null;
  const nAttach = (isNearMiss || isClinicIncident) ? 2 : n;

  return (
    <>
      {/* Event-type summary banner — type is now selected in Step 1 */}
      <div style={{
        marginBottom: 20, padding: '8px 12px',
        background: 'var(--surface-2)', border: '1px solid var(--border)',
        borderRadius: 8, fontSize: 13, color: 'var(--text-2)',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <span style={{
          display: 'inline-block', padding: '2px 8px', borderRadius: 999,
          background: 'var(--brand-weak)', color: 'var(--brand-ink)',
          fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em',
        }}>{eventTypes.find(t => t.value === form.EventType)?.label || 'Event'}</span>
        <span>{eventTypes.find(t => t.value === form.EventType)?.hint}</span>
      </div>

      {/* Treatment cascade — only when EventType is Treatment */}
      {isTreatment && (
        <>
          <div style={SECTION_STYLE}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <SectionHead n={nTreat} title="Treatment & severity" sub="Pick the treatment type, then the severity code. The incident-category dropdown and required documentation will update accordingly." />
              {onOpenGuide && (
                <button className="btn" type="button" onClick={onOpenGuide} style={{ flexShrink: 0 }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
                  Code reference
                </button>
              )}
            </div>
            <div className="field-row">
              <div className="field">
                <label htmlFor="Department">Treatment type <span style={{ color: 'var(--danger)' }}>*</span></label>
                <SearchableSelect
                  id="Department" name="Department"
                  value={form.Department} onChange={handleChange}
                  placeholder="Select treatment…"
                  options={treatmentTypes.map(t => ({
                    value: t.value,
                    label: t.label,
                    sub:   t.description,
                  }))}
                />
                {errors.Department && <div className="field-error">{errors.Department}</div>}
              </div>
              {treatmentHasCodes(form.Department) && (
              <div className="field">
                <label htmlFor="Severity">Severity code <span style={{ color: 'var(--danger)' }}>*</span></label>
                {severityCodes.length === 0 ? (
                  // Coded treatment with NO codes to pick = admin misconfiguration.
                  // Blocking (validateStep2 sets errors.Severity; the server 400s
                  // too) — focusable so the first-error focus lands here.
                  <div id="Severity" tabIndex={-1} role="alert" className="callout danger" style={{ fontSize: 12.5 }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    <div>{SEVERITY_NOT_CONFIGURED}</div>
                  </div>
                ) : (
                  <>
                    <SearchableSelect
                      id="Severity" name="Severity"
                      value={form.Severity} onChange={handleChange}
                      placeholder="Select code…"
                      options={severityCodes.map(c => ({ value: c.value, label: c.label }))}
                    />
                    {errors.Severity && <div className="field-error">{errors.Severity}</div>}
                  </>
                )}
              </div>
              )}
            </div>

            {showSubtype && (
              <div className="field">
                <label htmlFor="TreatmentSubtype">{form.Department} subtype <span style={{ color: 'var(--danger)' }}>*</span></label>
                <SearchableSelect
                  id="TreatmentSubtype" name="TreatmentSubtype"
                  value={form.TreatmentSubtype}
                  onChange={e => {
                    setForm(prev => ({
                      ...prev,
                      TreatmentSubtype: e.target.value,
                      TreatmentSubtypeOther: e.target.value === SUBTYPE_OTHER ? prev.TreatmentSubtypeOther : '',
                      IncidentCategory: '',
                      IncidentCategoryOther: '',
                    }));
                    setErrors(prev => ({ ...prev, TreatmentSubtype: undefined, TreatmentSubtypeOther: undefined }));
                  }}
                  placeholder="Select subtype…"
                  options={[
                    ...subtypeOptions.map(s => ({ value: s, label: s })),
                    { value: SUBTYPE_OTHER, label: SUBTYPE_OTHER },
                  ]}
                />
                {form.TreatmentSubtype === SUBTYPE_OTHER && (
                  <>
                    <CharCountInput fieldKey="TreatmentSubtypeOther" style={{ marginTop: 8 }}
                      id="TreatmentSubtypeOther" name="TreatmentSubtypeOther"
                      value={form.TreatmentSubtypeOther}
                      onChange={handleChange}
                      placeholder="Describe the subtype…" />
                    {errors.TreatmentSubtypeOther && <div className="field-error">{errors.TreatmentSubtypeOther}</div>}
                  </>
                )}
                {errors.TreatmentSubtype && <div className="field-error">{errors.TreatmentSubtype}</div>}
              </div>
            )}

            {showCategoryDropdown && (
              <div className="field">
                {/* Mandatory whenever there is something to pick (§4) — the list
                    itself is the deadlock guard: no options, no asterisk. */}
                <label htmlFor="IncidentCategory">Incident category <span style={{ color: 'var(--danger)' }}>*</span></label>
                <SearchableSelect
                  id="IncidentCategory" name="IncidentCategory"
                  value={form.IncidentCategory} onChange={handleChange}
                  placeholder="Select category…"
                  options={categories.map(c => ({ value: c, label: c }))}
                />
                {form.IncidentCategory === 'Other' && (
                  <>
                    <CharCountInput fieldKey="IncidentCategoryOther" style={{ marginTop: 8 }}
                      id="IncidentCategoryOther" name="IncidentCategoryOther"
                      value={form.IncidentCategoryOther} onChange={handleChange}
                      placeholder="Describe the incident category…" />
                    {errors.IncidentCategoryOther && <div className="field-error">{errors.IncidentCategoryOther}</div>}
                  </>
                )}
                {errors.IncidentCategory && <div className="field-error">{errors.IncidentCategory}</div>}
              </div>
            )}

            <div className="field" style={showReviewDate ? undefined : { marginBottom: 0 }}>
              <label htmlFor="ServiceCarriedOut">Service carried out</label>
              <CharCountInput fieldKey="ServiceCarriedOut" id="ServiceCarriedOut" name="ServiceCarriedOut" type="text"
                value={form.ServiceCarriedOut} onChange={handleChange}
                placeholder="e.g. LHR — full legs · 1064 nm" />
            </div>

            {/* Review date — Code 3–5: when the client was / will be seen. Past
                and future dates are both fine (the review may have happened). */}
            {showReviewDate && (
              <div className="field-row">
                <div className="field" style={{ marginBottom: 0 }}>
                  <label htmlFor="ReviewDate">Review date <span style={{ color: 'var(--danger)' }}>*</span></label>
                  <DateInput id="ReviewDate" name="ReviewDate"
                    value={form.ReviewDate} onChange={handleChange}
                    style={errors.ReviewDate ? ERR_STYLE : undefined} />
                  {errors.ReviewDate && <div className="field-error">{errors.ReviewDate}</div>}
                  <div className="field-hint">{REVIEW_DATE_HELP}</div>
                </div>
              </div>
            )}
          </div>

          {/* Required to submit — every Treatment report captures the consent
              form now (upload it, or record that there is none and why); LHR
              Code 1–2 additionally capture the laser settings. Unlike the
              requirements panel below (complete-later), these block submission. */}
          <div style={SECTION_STYLE}>
            <SectionHead n={nSubmit} title="Required to submit"
              sub={laserEvidence
                ? 'The consent form and the laser settings are captured now — add each one, or record that it doesn\'t exist.'
                : 'The consent form is captured now — upload it, or record that there is none.'} />
            <div className="field-row">
              <div className="field" id="ConsentEvidence" tabIndex={-1} style={{ outline: 'none', marginBottom: 0, gridColumn: laserEvidence ? undefined : '1 / -1' }}>
                <label>Consent form <span style={{ color: 'var(--danger)' }}>*</span></label>
                <RequiredSlots
                  requiredUploads={['consent_form']}
                  attachedFiles={attachedFiles}
                  addAttachment={addAttachment}
                  removeFile={removeFile}
                  formatBytes={formatBytes}
                  incidentDate={form.DateofIncident}
                />
                {/* Adding a consent file un-ticks this (never both at once); removing
                    the file again does not re-tick it. */}
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, cursor: hasConsentUpload ? 'default' : 'pointer', opacity: hasConsentUpload ? 0.45 : 1 }}>
                  <input type="checkbox" className="native"
                    checked={form.ConsentWaived === true}
                    disabled={hasConsentUpload}
                    onChange={e => {
                      const on = e.target.checked;
                      setForm(prev => ({ ...prev, ConsentWaived: on }));
                      setErrors(prev => (prev.ConsentEvidence ? { ...prev, ConsentEvidence: undefined } : prev));
                    }} />
                  <span style={{ fontSize: 13 }}>No consent form</span>
                </label>
                {form.ConsentWaived && !hasConsentUpload && (
                  <div style={{ marginTop: 8 }}>
                    <label htmlFor="ConsentWaiverReason" style={{ fontSize: 12 }}>Why is there no consent form? <span style={{ color: 'var(--danger)' }}>*</span></label>
                    <CharCountTextarea fieldKey="consent_waiver_reason" id="ConsentWaiverReason" name="ConsentWaiverReason"
                      value={form.ConsentWaiverReason} onChange={handleChange}
                      style={{ minHeight: 64 }}
                      placeholder="e.g. Form not completed at consultation; form lost during clinic move…" />
                    <div className="field-hint">Recorded on the case as a waiver, with your name and today's date.</div>
                  </div>
                )}
                {errors.ConsentEvidence && !consentOk && <div className="field-error">{errors.ConsentEvidence}</div>}
              </div>
              {laserEvidence && (
                <div className="field" id="LaserSettingsEvidence" tabIndex={-1} style={{ outline: 'none', marginBottom: 0 }}>
                  <label htmlFor="LaserSettingsUsed">Laser settings used <span style={{ color: 'var(--danger)' }}>*</span></label>
                  <CharCountInput fieldKey="laser_settings" id="LaserSettingsUsed" type="text"
                    value={form.TypeSpecific.laser_settings || ''}
                    disabled={form.TypeSpecific.no_laser_settings === true}
                    onChange={e => {
                      handleTypeSpecificChange('laser_settings', e.target.value);
                      if (e.target.value.trim() && form.TypeSpecific.no_laser_settings) handleTypeSpecificChange('no_laser_settings', false);
                    }}
                    placeholder="e.g. 1064 nm · 30 J/cm² · 10 mm spot" />
                  {/* A CHECKED box is never disabled — the flag must always be
                      untickable, even if Code 5 round-trips filled the text. */}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, cursor: 'pointer', opacity: String(form.TypeSpecific.laser_settings || '').trim() ? 0.45 : 1 }}>
                    <input type="checkbox" className="native"
                      checked={form.TypeSpecific.no_laser_settings === true}
                      disabled={form.TypeSpecific.no_laser_settings !== true && !!String(form.TypeSpecific.laser_settings || '').trim()}
                      onChange={e => handleTypeSpecificChange('no_laser_settings', e.target.checked)} />
                    <span style={{ fontSize: 13 }}>No settings recorded</span>
                  </label>
                  {errors.LaserSettingsEvidence && !laserOk && <div className="field-error">{errors.LaserSettingsEvidence}</div>}
                </div>
              )}
            </div>
          </div>

          {/* Requirements panel — required (complete-later) items and/or the
              optional details the matrix offers for this treatment/code. */}
          {showReqPanel && (
            <div style={SECTION_STYLE}>
              <SectionHead n={nReq}
                title={hasRequired
                  ? (treatmentHasCodes(form.Department) ? 'Required for this code' : 'Required documents')
                  : 'Additional details'}
                sub={hasRequired
                  ? 'The details and documents this case needs to be complete — not to submit it. Add what you have now; anything missing can be added to the case later.'
                  : 'Optional for this code — record what you know now; anything can be added to the case later.'} />
              {hasRequired && (
                <div className="callout brand" style={{ marginBottom: 14 }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  <div style={{ fontSize: 13 }}>
                    <strong>{requirements.priority === 'high' ? 'High priority.' : 'Additional requirements.'}</strong>
                    {requirements.notify.length > 0 && <> Will involve: {requirements.notify.join(' · ')}.</>}
                  </div>
                </div>
              )}

              {(extraFieldKeys.length > 0 || hasOptional) && (
                <div style={{ marginBottom: panelUploads.length > 0 ? 12 : 0 }}>
                  {extraFieldKeys.length > 0 && (
                    <>
                      <div style={BLOCK_LABEL}>Extra fields</div>
                      <div className="field-row">
                        {extraFieldKeys.map(k => (
                          <ExtraField key={k} fieldKey={k} value={form.TypeSpecific[k]} onChange={handleTypeSpecificChange}
                            required={submitRequiredSet.has(k)} error={errors[`ts-${k}`]} />
                        ))}
                      </div>
                    </>
                  )}
                  {hasOptional && (
                    <>
                      {/* Subtle — optional never reads like a requirement. */}
                      <div style={{ ...BLOCK_LABEL, fontWeight: 500, marginTop: extraFieldKeys.length > 0 ? 4 : 0 }}>Optional</div>
                      <div className="field-row">
                        {optionalFieldKeys.map(k => (
                          <ExtraField key={k} fieldKey={k} value={form.TypeSpecific[k]} onChange={handleTypeSpecificChange} />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {panelUploads.length > 0 && (
                <div>
                  <div style={{ ...BLOCK_LABEL, marginBottom: 4 }}>Documents</div>
                  {/* Stated ONCE here — the slots below carry no per-row instruction. */}
                  <p style={{ fontSize: 12.5, color: 'var(--text-3)', margin: '0 0 10px' }}>
                    These will be required for this case. If you have any of them at hand, upload them using the buttons below.
                  </p>
                  <RequiredSlots
                    requiredUploads={panelUploads}
                    attachedFiles={attachedFiles}
                    addAttachment={addAttachment}
                    removeFile={removeFile}
                    formatBytes={formatBytes}
                    incidentDate={form.DateofIncident}
                  />
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Incident description — Treatment only; NearMiss has its own description fields */}
      {isTreatment && (
        <div style={SECTION_STYLE}>
          <SectionHead n={nDesc} title="Incident description" sub="Describe what happened objectively." />
          <CharCountTextarea fieldKey="MedicalNotes" id="MedicalNotes" name="MedicalNotes" style={{ minHeight: 140 }}
            value={form.MedicalNotes} onChange={handleChange}
            placeholder="Describe the adverse event, symptoms, and any immediate actions taken…" />
          <div className="field-hint">Be factual · avoid speculation</div>
        </div>
      )}

      {/* NearMiss form — replaces the treatment cascade + description for near-miss reports */}
      {isNearMiss && (
        <NearMissSection
          form={form}
          errors={errors}
          handleChange={handleChange}
        />
      )}

      {/* ClinicIncident form — replaces the treatment cascade + description */}
      {isClinicIncident && (
        <ClinicIncidentSection
          form={form}
          errors={errors}
          handleChange={handleChange}
        />
      )}

      {/* Attachments — required slots + ad-hoc uploads, one combined list */}
      <AttachmentsSection
        n={nAttach}
        requiredUploads={isTreatment ? submitRequiredUploads : requiredUploads}
        attachedFiles={attachedFiles}
        attachmentOptions={attachmentOptions}
        addAttachment={addAttachment}
        removeFile={removeFile}
        formatBytes={formatBytes}
        incidentDate={form.DateofIncident}
      />
    </>
  );
}

// ── One matrix extra field, typed by FIELD_META[fieldKey].type ──────────────
// Value is stored raw in the TypeSpecific bag: checkbox → boolean, yesno →
// null | true | false, everything else → string. Wide controls (checkbox,
// yes/no, textarea) span both grid columns. The yes/no radiogroup is a div
// (not labelable), so its label names it via aria-labelledby instead of htmlFor.
function ExtraField({ fieldKey: k, value, onChange, required = false, error = null }) {
  const meta = FIELD_META[k];
  if (!meta) return null;
  const wide = meta.type === 'checkbox' || meta.type === 'yesno' || meta.type === 'textarea';
  const text = value == null ? '' : value;
  const id      = `ts-${k}`;
  const labelId = `${id}-label`;
  const labelFor = meta.type === 'checkbox' || meta.type === 'yesno' ? undefined : id;
  return (
    <div className="field" style={{ gridColumn: wide ? '1 / -1' : undefined }}>
      <label id={labelId} htmlFor={labelFor}>{meta.label}{required && <> <span style={{ color: 'var(--danger)' }}>*</span></>}</label>
      {meta.type === 'select' ? (
        <SearchableSelect
          id={id} name={k} value={text}
          onChange={e => onChange(k, e.target.value)}
          placeholder="Select…"
          options={meta.options.map(o => ({ value: o, label: o }))}
        />
      ) : meta.type === 'checkbox' ? (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: 'pointer' }}>
          <input type="checkbox" className="native" checked={value === true}
            onChange={e => onChange(k, e.target.checked)} />
          <span style={{ fontSize: 13 }}>Confirmed</span>
        </label>
      ) : meta.type === 'yesno' ? (
        <div style={{ padding: '4px 0' }}>
          <YesNoSwitch
            id={id}
            ariaLabelledby={labelId}
            value={value}
            onChange={next => onChange(k, next)}
          />
        </div>
      ) : meta.type === 'date' ? (
        <DateInput
          id={id}
          name={k}
          value={text}
          onChange={e => onChange(k, e.target.value)}
        />
      ) : meta.type === 'datetime-local' ? (
        <DateTimeExtraField
          fieldKey={k}
          value={text}
          onChange={onChange}
        />
      ) : meta.type === 'textarea' ? (
        <CharCountTextarea fieldKey={k} id={id} name={k} value={text} style={{ minHeight: 80 }}
          onChange={e => onChange(k, e.target.value)} />
      ) : (
        <CharCountInput fieldKey={k} id={id} name={k} type={meta.type} value={text}
          onChange={e => onChange(k, e.target.value)} />
      )}
      {error && <div className="field-error">{error}</div>}
    </div>
  );
}

// ── NearMiss Step 2 body ─────────────────────────────────────────────────
function NearMissSection({ form, errors, handleChange }) {
  // Refusal Register (Compliance only) replaces the what/why/action body with the
  // five register items — refusal_register_logic.md.
  const isRefusal = form.NM_Category === REFUSAL_REGISTER_CATEGORY;
  return (
    <div style={{ marginBottom: 24, paddingBottom: 24, borderBottom: '1px solid var(--border)' }}>
      <SectionHead
        n={1}
        title={isRefusal ? 'Refusal details' : 'Near-miss details'}
        sub={isRefusal
          ? 'A booking or procedure that was refused — recorded in the refusal register.'
          : "What nearly happened, why, and what's being done about it."} />

      {/* Reporting function + category */}
      <div className="field-row">
        <div className="field">
          <label htmlFor="NM_ReportingFunction">Reporting function <span style={{ color: 'var(--danger)' }}>*</span></label>
          <SearchableSelect
            id="NM_ReportingFunction" name="NM_ReportingFunction"
            value={form.NM_ReportingFunction} onChange={handleChange}
            placeholder="Select function…"
            options={NEAR_MISS_REPORTING_FUNCTIONS.map(f => ({ value: f.value, label: f.label }))}
          />
          {errors.NM_ReportingFunction && <div className="field-error">{errors.NM_ReportingFunction}</div>}
        </div>
        <div className="field">
          <label htmlFor="NM_Category">Category <span style={{ color: 'var(--danger)' }}>*</span></label>
          <SearchableSelect
            id="NM_Category" name="NM_Category"
            value={form.NM_Category} onChange={handleChange}
            placeholder="Select category…"
            options={nearMissCategoriesFor(form.NM_ReportingFunction).map(c => ({ value: c, label: c }))}
          />
          {errors.NM_Category && <div className="field-error">{errors.NM_Category}</div>}
        </div>
      </div>

      {form.NM_Category === 'Other' && (
        <div className="field">
          <label htmlFor="NM_CategoryOther">Describe category <span style={{ color: 'var(--danger)' }}>*</span></label>
          <CharCountInput fieldKey="NM_CategoryOther" id="NM_CategoryOther" name="NM_CategoryOther" type="text"
            value={form.NM_CategoryOther} onChange={handleChange}
            placeholder="Describe the category…" />
          {errors.NM_CategoryOther && <div className="field-error">{errors.NM_CategoryOther}</div>}
        </div>
      )}

      {isRefusal ? (
        <RefusalRegisterFields form={form} errors={errors} handleChange={handleChange} />
      ) : (
        <>
        {/* What / cause / action — three short textareas */}
        <div className="field">
          <label htmlFor="NM_Description">What nearly went wrong <span style={{ color: 'var(--danger)' }}>*</span></label>
          <CharCountTextarea fieldKey="NM_Description" id="NM_Description" name="NM_Description" style={{ minHeight: 90 }}
            value={form.NM_Description} onChange={handleChange}
            placeholder="Objective factual account — what was the hazard, what was the close call?" />
          {errors.NM_Description && <div className="field-error">{errors.NM_Description}</div>}
        </div>

        <div className="field">
          <label htmlFor="NM_Cause">Cause <span style={{ color: 'var(--danger)' }}>*</span></label>
          <CharCountTextarea fieldKey="NM_Cause" id="NM_Cause" name="NM_Cause" style={{ minHeight: 70 }}
            value={form.NM_Cause} onChange={handleChange}
            placeholder="Why did this nearly happen? Process, training, equipment, environment…" />
          {errors.NM_Cause && <div className="field-error">{errors.NM_Cause}</div>}
        </div>

        <div className="field">
          <label htmlFor="NM_CorrectiveAction">Corrective action <span style={{ color: 'var(--danger)' }}>*</span></label>
          <CharCountTextarea fieldKey="NM_CorrectiveAction" id="NM_CorrectiveAction" name="NM_CorrectiveAction" style={{ minHeight: 70 }}
            value={form.NM_CorrectiveAction} onChange={handleChange}
            placeholder="What will be done to prevent this from becoming an incident?" />
          {errors.NM_CorrectiveAction && <div className="field-error">{errors.NM_CorrectiveAction}</div>}
        </div>

        {/* Assignee + target date */}
        <div className="field-row">
          <div className="field">
            <label htmlFor="NM_AssigneeRole">Assigned to <span style={{ color: 'var(--danger)' }}>*</span></label>
            <SearchableSelect
              id="NM_AssigneeRole" name="NM_AssigneeRole"
              value={form.NM_AssigneeRole} onChange={handleChange}
              placeholder="Select assignee…"
              options={NEAR_MISS_ASSIGNEES.map(a => ({ value: a.value, label: a.label }))}
            />
            {errors.NM_AssigneeRole && <div className="field-error">{errors.NM_AssigneeRole}</div>}
          </div>
          <div className="field">
            <label htmlFor="NM_TargetResolutionDate">Target resolution date <span style={{ color: 'var(--danger)' }}>*</span></label>
            <DateInput id="NM_TargetResolutionDate" name="NM_TargetResolutionDate"
              value={form.NM_TargetResolutionDate} onChange={handleChange}
              min={todayIso()} />
            {errors.NM_TargetResolutionDate && <div className="field-error">{errors.NM_TargetResolutionDate}</div>}
          </div>
        </div>

        {form.NM_AssigneeRole === 'Other' && (
          <div className="field">
            <label htmlFor="NM_AssigneeOther">Specify assignee <span style={{ color: 'var(--danger)' }}>*</span></label>
            <CharCountInput fieldKey="NM_AssigneeOther" id="NM_AssigneeOther" name="NM_AssigneeOther" type="text"
              value={form.NM_AssigneeOther} onChange={handleChange}
              placeholder="Name or team…" />
            {errors.NM_AssigneeOther && <div className="field-error">{errors.NM_AssigneeOther}</div>}
          </div>
        )}

        {/* Potential severity tier — optional, set by reviewer or reporter if known */}
        <div className="field">
          <label htmlFor="NM_PotentialSeverityTier">Potential severity (if it had occurred) <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-3)' }}>· optional</span></label>
          <div style={{ display: 'flex', gap: 8 }}>
            {NEAR_MISS_SEVERITY_TIERS.map(t => (
              <button key={t.value} type="button"
                onClick={() => handleChange({ target: { name: 'NM_PotentialSeverityTier', value: form.NM_PotentialSeverityTier === t.value ? '' : t.value } })}
                style={{
                  flex: 1, padding: '10px 14px', borderRadius: 8,
                  border: '1px solid', borderColor: form.NM_PotentialSeverityTier === t.value ? 'var(--brand)' : 'var(--border)',
                  background: form.NM_PotentialSeverityTier === t.value ? 'var(--brand-weak)' : 'var(--surface)',
                  cursor: 'pointer', textAlign: 'left',
                }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{t.label}</div>
                <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{t.hint}</div>
              </button>
            ))}
          </div>
          <div className="field-hint">Use the reviewer's judgement, not the reporter's — leave blank if unsure.</div>
        </div>
        </>
      )}
    </div>
  );
}

// ── Refusal Register fields (Compliance near miss) ────────────────────────
// The five items a refusals register has to carry for an audit: when the request
// was made, why it was refused, who was refused, what was done, and by whom.
// Spec: refusal_register_logic.md. No cause / corrective action / assignee /
// target date — the refusal WAS the action, and the entry closes on submission.
function RefusalRegisterFields({ form, errors, handleChange }) {
  return (
    <>
      {/* 1. Date and time — the exact moment of the booking request or enquiry.
             Its date becomes the case's date of incident, so the two can't drift. */}
      <div className="field">
        <label htmlFor="RR_RequestedAt">Date and time of the request <span style={{ color: 'var(--danger)' }}>*</span></label>
        <DateTimeExtraField
          fieldKey="RR_RequestedAt"
          id="RR_RequestedAt"
          value={form.RR_RequestedAt}
          onChange={(key, value) => handleChange({ target: { name: key, value } })}
        />
        <div className="field-hint">When the booking request or enquiry was made — this sets the case's date of incident.</div>
        {errors.RR_RequestedAt && <div className="field-error">{errors.RR_RequestedAt}</div>}
      </div>

      {/* 2. Details of the incident → incident_description (immutable) */}
      <div className="field">
        <label htmlFor="NM_Description">Details of the incident <span style={{ color: 'var(--danger)' }}>*</span></label>
        <CharCountTextarea fieldKey="NM_Description" id="NM_Description" name="NM_Description" style={{ minHeight: 90 }}
          value={form.NM_Description} onChange={handleChange}
          placeholder="Why was the booking or procedure refused? e.g. failure to provide acceptable ID under the Challenge 25 policy." />
        {errors.NM_Description && <div className="field-error">{errors.NM_Description}</div>}
      </div>

      {/* 3. Who was refused — a brief identifier, never an ID document */}
      <div className="field">
        <label htmlFor="RR_BuyerDescription">Description of the potential buyer <span style={{ color: 'var(--danger)' }}>*</span></label>
        <CharCountTextarea fieldKey="RR_BuyerDescription" id="RR_BuyerDescription" name="RR_BuyerDescription" style={{ minHeight: 70 }}
          value={form.RR_BuyerDescription} onChange={handleChange}
          placeholder="Brief physical description or identifier — enough to recognise a repeat attempt." />
        <div className="field-hint">Keep it brief and factual. Do not record ID numbers, dates of birth or contact details.</div>
        {errors.RR_BuyerDescription && <div className="field-error">{errors.RR_BuyerDescription}</div>}
      </div>

      {/* 4. Action taken — the confirmation an auditor looks for */}
      <div className="field">
        <label htmlFor="RR_ActionTaken">Action taken <span style={{ color: 'var(--danger)' }}>*</span></label>
        <CharCountTextarea fieldKey="RR_ActionTaken" id="RR_ActionTaken" name="RR_ActionTaken" style={{ minHeight: 70 }}
          value={form.RR_ActionTaken} onChange={handleChange}
          placeholder="Confirm the appointment was denied and that no arrangements were made." />
        {errors.RR_ActionTaken && <div className="field-error">{errors.RR_ActionTaken}</div>}
      </div>

      {/* 5. Who refused it — the case's own staff field, shown here because the
             register needs it; editing it here edits the same value as Step 1. */}
      <div className="field">
        <label htmlFor="StaffMemberConcerned">Staff member who refused the request <span style={{ color: 'var(--danger)' }}>*</span></label>
        <CharCountInput fieldKey="StaffMemberConcerned" id="StaffMemberConcerned" name="StaffMemberConcerned" type="text"
          value={form.StaffMemberConcerned} onChange={handleChange}
          placeholder="Name or initials" />
        <div className="field-hint">Whoever handled and refused the request. This is the case's staff member (Step 1).</div>
        {errors.StaffMemberConcerned && <div className="field-error">{errors.StaffMemberConcerned}</div>}
      </div>

      <div style={{
        marginTop: 4, padding: '10px 12px', borderRadius: 8,
        background: 'var(--brand-weak)', border: '1px solid var(--border)',
        fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5,
      }}>
        On submit this is tagged <strong>RefusalRegister</strong> and closed automatically —
        no corrective action to chase. Notifications go out as normal, and the entry stays
        in the register for audit. Do not attach ID documents or photographs.
      </div>
    </>
  );
}

// ── ClinicIncident Step 2 body ────────────────────────────────────────────
// Non-treatment incident at a clinic (accident at work / public liability).
// Captures the incident facts only — investigation, corrective action and
// review happen on the case afterwards. Spec: clinic_incident_logic.md.
function ClinicIncidentSection({ form, errors, handleChange }) {
  return (
    <div style={{ marginBottom: 24, paddingBottom: 24, borderBottom: '1px solid var(--border)' }}>
      <SectionHead n={1} title="Incident details" sub="What happened, who was hurt, and what was done at the time." />

      {/* Incident type + injured person status */}
      <div className="field-row">
        <div className="field">
          <label htmlFor="CI_IncidentType">Type of incident <span style={{ color: 'var(--danger)' }}>*</span></label>
          <SearchableSelect
            id="CI_IncidentType" name="CI_IncidentType"
            value={form.CI_IncidentType} onChange={handleChange}
            placeholder="Select type…"
            options={CLINIC_INCIDENT_TYPES.map(c => ({ value: c, label: c }))}
          />
          {errors.CI_IncidentType && <div className="field-error">{errors.CI_IncidentType}</div>}
        </div>
        <div className="field">
          <label htmlFor="CI_PersonStatus">Injured person <span style={{ color: 'var(--danger)' }}>*</span></label>
          <SearchableSelect
            id="CI_PersonStatus" name="CI_PersonStatus"
            value={form.CI_PersonStatus} onChange={handleChange}
            placeholder="Who was hurt?"
            options={CLINIC_INCIDENT_PERSON_STATUS.map(p => ({ value: p.value, label: p.label }))}
          />
          {errors.CI_PersonStatus && <div className="field-error">{errors.CI_PersonStatus}</div>}
        </div>
      </div>

      {form.CI_IncidentType === 'Other' && (
        <div className="field">
          <label htmlFor="CI_IncidentTypeOther">Describe the incident type <span style={{ color: 'var(--danger)' }}>*</span></label>
          <CharCountInput fieldKey="CI_IncidentTypeOther" id="CI_IncidentTypeOther" name="CI_IncidentTypeOther" type="text"
            value={form.CI_IncidentTypeOther} onChange={handleChange}
            placeholder="Describe what kind of incident…" />
          {errors.CI_IncidentTypeOther && <div className="field-error">{errors.CI_IncidentTypeOther}</div>}
        </div>
      )}

      {/* Injured person details (optional PII) */}
      <div className="field-row">
        <div className="field">
          <label htmlFor="CI_InjuredName">Injured person name</label>
          <CharCountInput fieldKey="CI_InjuredName" id="CI_InjuredName" name="CI_InjuredName" type="text"
            value={form.CI_InjuredName} onChange={handleChange} placeholder="Full name (if known)" />
        </div>
        <div className="field">
          <label htmlFor="CI_InjuredContact">Injured person contact</label>
          <CharCountInput fieldKey="CI_InjuredContact" id="CI_InjuredContact" name="CI_InjuredContact" type="text"
            value={form.CI_InjuredContact} onChange={handleChange} placeholder="Phone or email (if known)" />
        </div>
      </div>

      {/* What happened → medical_notes */}
      <div className="field">
        <label htmlFor="CI_Description">What happened <span style={{ color: 'var(--danger)' }}>*</span></label>
        <CharCountTextarea fieldKey="CI_Description" id="CI_Description" name="CI_Description" style={{ minHeight: 110 }}
          value={form.CI_Description} onChange={handleChange}
          placeholder="Objective factual account — what happened, where, and how?" />
        {errors.CI_Description && <div className="field-error">{errors.CI_Description}</div>}
      </div>

      <div className="field">
        <label htmlFor="CI_InjuryNature">Nature of injuries</label>
        <CharCountTextarea fieldKey="CI_InjuryNature" id="CI_InjuryNature" name="CI_InjuryNature" style={{ minHeight: 70 }}
          value={form.CI_InjuryNature} onChange={handleChange}
          placeholder="Describe any injuries sustained…" />
      </div>

      <div className="field">
        <label htmlFor="CI_ImmediateResponse">Immediate response / first aid</label>
        <CharCountTextarea fieldKey="CI_ImmediateResponse" id="CI_ImmediateResponse" name="CI_ImmediateResponse" style={{ minHeight: 70 }}
          value={form.CI_ImmediateResponse} onChange={handleChange}
          placeholder="What was done at the time — first aid, made safe, escalated…" />
      </div>

      {/* Witness (optional) */}
      <div className="field-row">
        <div className="field">
          <label htmlFor="CI_WitnessName">Witness name</label>
          <CharCountInput fieldKey="CI_WitnessName" id="CI_WitnessName" name="CI_WitnessName" type="text"
            value={form.CI_WitnessName} onChange={handleChange} placeholder="If applicable" />
        </div>
        <div className="field">
          <label htmlFor="CI_WitnessContact">Witness contact</label>
          <CharCountInput fieldKey="CI_WitnessContact" id="CI_WitnessContact" name="CI_WitnessContact" type="text"
            value={form.CI_WitnessContact} onChange={handleChange} placeholder="If applicable" />
        </div>
      </div>

      <div className="field" style={{ marginBottom: 0 }}>
        <label htmlFor="CI_WitnessStatement">Witness statement</label>
        <CharCountTextarea fieldKey="CI_WitnessStatement" id="CI_WitnessStatement" name="CI_WitnessStatement" style={{ minHeight: 70 }}
          value={form.CI_WitnessStatement} onChange={handleChange}
          placeholder="Account from the witness, if one was taken…" />
      </div>
    </div>
  );
}

// ── Attachments section ──────────────────────────────────────────────────
// ── Required document slots ──────────────────────────────────────────────
// Click-to-upload slots for a treatment/code's required documents. Rendered
// inside the "Required for this code" panel so required docs sit with the rest
// of that code's requirements; the Attachments section below is for any extra
// optional files. Categories with per-file metadata (post photo, training
// certificate) open the two-step SlotUploadModal instead of the bare picker.
function RequiredSlots({ requiredUploads, attachedFiles, addAttachment, removeFile, formatBytes, incidentDate = null }) {
  const slotFileRef     = useRef(null);
  const slotCategoryRef = useRef(null);
  const [typedCat, setTypedCat] = useState(null);  // prose slot with the type-it-in pop-up open
  const [metaCat,  setMetaCat]  = useState(null);  // slot with the file + details modal open

  const filesByCategory = attachedFiles.reduce((acc, item, idx) => {
    (acc[item.category] ||= []).push({ item, idx });
    return acc;
  }, {});

  function openSlotPicker(catKey) {
    // Prose categories (therapist statement) open a type-it-in pop-up that
    // saves as a .txt pending file; the pop-up offers the OS picker as well.
    if (TYPED_TEXT_CATEGORIES.has(catKey)) {
      setTypedCat(catKey);
      return;
    }
    // Categories that carry metadata (when the photo was taken, who trained
    // whom) go through the two-step modal so the detail is captured with the file.
    if (categoryNeedsMetadata(catKey)) {
      setMetaCat(catKey);
      return;
    }
    slotCategoryRef.current = catKey;
    slotFileRef.current?.click();
  }
  function onSlotFileChosen(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    const catKey = slotCategoryRef.current;
    slotCategoryRef.current = null;
    if (!files.length || !catKey) return;
    for (const file of files) addAttachment({ file, category: catKey, otherLabel: null });
  }

  if (!requiredUploads.length) return null;

  return (
    <>
      <input ref={slotFileRef} type="file" multiple style={{ display: 'none' }} onChange={onSlotFileChosen} />
      {typedCat && (
        <TypedTextFileModal
          title={ATTACHMENT_CATEGORY_LABELS[typedCat] || typedCat}
          placeholder="Type the statement…"
          onClose={() => setTypedCat(null)}
          onChooseFile={() => {
            slotCategoryRef.current = typedCat;
            setTypedCat(null);
            slotFileRef.current?.click();
          }}
          onSave={(text) => {
            const cat  = typedCat;
            const file = new File([text], `${categoryToken({ category: cat, otherLabel: null })}.txt`, { type: 'text/plain' });
            addAttachment({ file, category: cat, otherLabel: null });
            setTypedCat(null);
          }}
        />
      )}
      {metaCat && (
        <SlotUploadModal
          open
          category={metaCat}
          label={ATTACHMENT_CATEGORY_LABELS[metaCat] || metaCat}
          incidentDate={incidentDate || null}
          onCancel={() => setMetaCat(null)}
          onConfirm={({ files, metadata }) => {
            const cat = metaCat;
            for (const file of files) addAttachment({ file, category: cat, otherLabel: null, metadata });
            setMetaCat(null);
          }}
        />
      )}
      <div className="attach-slot-grid">
        {requiredUploads.map(catKey => {
          const matches   = filesByCategory[catKey] || [];
          const satisfied = matches.length > 0;
          const label     = ATTACHMENT_CATEGORY_LABELS[catKey] || catKey;
          const typed     = TYPED_TEXT_CATEGORIES.has(catKey);
          return (
            <div key={catKey} className="attach-slot" data-satisfied={satisfied ? 'true' : 'false'}>
              <button
                type="button"
                className="attach-slot-head"
                onClick={() => openSlotPicker(catKey)}
                title={satisfied
                  ? `${label} — click to add another${typed ? '' : ' file'}`
                  : (typed ? `${label} — click to type it in or attach a file` : `${label} — click to upload`)}
                aria-label={satisfied ? `Add another ${label} file` : `Upload ${label}`}
              >
                <SlotStateIcon state={satisfied ? 'done' : 'required'} />
                <span className="attach-slot-label">{label}</span>
                {satisfied && (
                  <span className="attach-slot-status">{matches.length} attached</span>
                )}
              </button>
              {matches.length > 0 && (
                <div style={{ padding: '0 12px 10px 42px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {matches.map(({ item, idx }, i) => (
                    <FileRow key={idx} item={item} onRemove={() => removeFile(idx)} formatBytes={formatBytes} dupeIndex={i} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

function AttachmentsSection({ n, requiredUploads, attachedFiles, attachmentOptions, addAttachment, removeFile, formatBytes, incidentDate = null }) {
  const [pickerOpen, setPickerOpen] = useState(false);

  // "Other" attachments (not matching any required slot — those live in the
  // "Required for this code" panel above)
  const requiredSet = new Set(requiredUploads);
  const otherAttachments = attachedFiles
    .map((item, idx) => ({ item, idx }))
    .filter(({ item }) => !requiredSet.has(item.category));

  return (
    <div>
      <SectionHead n={n} title="Additional attachments" sub={requiredUploads.length > 0
        ? 'Any other supporting files (optional). Documents needed for this case are in the sections above.'
        : 'Any supporting files you\'d like to include (optional).'} />

      {/* Optional / ad-hoc attachments */}
      {otherAttachments.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div className="attach-slot-grid">
            {(() => {
              // Mirror the submit-loop counter so previews of same-token "other"
              // attachments show the -2/-3 suffix that will be uploaded.
              const seen = new Map();
              return otherAttachments.map(({ item, idx }) => {
                const token = categoryToken(item);
                const dupeIndex = seen.get(token) || 0;
                seen.set(token, dupeIndex + 1);
                return (
                  <div key={idx} style={{
                    padding: '6px 12px', borderRadius: 8,
                    background: 'var(--surface-2)', border: '1px solid var(--border)',
                  }}>
                    <FileRow item={item} onRemove={() => removeFile(idx)} formatBytes={formatBytes} dupeIndex={dupeIndex} />
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}

      {/* Add picker */}
      {!pickerOpen ? (
        <button type="button" className="btn" onClick={() => setPickerOpen(true)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
            <path d="M12 5v14M5 12h14"/>
          </svg>
          Add attachment
        </button>
      ) : (
        <AddAttachmentPicker
          attachmentOptions={attachmentOptions}
          requiredSet={requiredSet}
          incidentDate={incidentDate}
          onAdd={(payload) => {
            addAttachment(payload);
            setPickerOpen(false);
          }}
          onCancel={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

// ── A single file row (filename preview + size + metadata + remove) ──────
// The metadata line is the ONE shared formatter (lib/attachmentMetadata.js),
// so the queued row reads exactly like the case-file pill it will become.
function FileRow({ item, onRemove, formatBytes, dupeIndex = 0 }) {
  const previewName = previewFileName('AE-NEW', item, dupeIndex);
  const originalDifferent = item.file.name !== previewName.replace(/^AE-NEW-/, '').replace(/-\d+(?=\.[^.]+$)/, '');
  const metaLine = attachmentMetadataLine(item.metadata, { formatDate: isoToDmy });
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        style={{ width: 13, height: 13, color: 'var(--brand)', flexShrink: 0 }}>
        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.41 17.41a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
      </svg>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}>
          {previewName}
        </div>
        <div style={{ fontSize: 10.5, color: 'var(--text-3)' }}>
          {[formatBytes(item.file.size), metaLine, originalDifferent ? `was: ${item.file.name}` : null].filter(Boolean).join(' · ')}
        </div>
      </div>
      <button type="button" onClick={onRemove} style={{
        background: 'none', border: 'none', cursor: 'pointer',
        color: 'var(--text-3)', fontSize: 16, lineHeight: 1, padding: '0 4px',
      }} title="Remove">×</button>
    </div>
  );
}

// ── Add-attachment picker (category first, then file) ────────────────────
function AddAttachmentPicker({ attachmentOptions, requiredSet, incidentDate = null, onAdd, onCancel }) {
  const [category, setCategory]     = useState('');
  const [otherLabel, setOtherLabel] = useState('');
  const [metaOpen, setMetaOpen]     = useState(false);   // two-step modal for metadata categories
  const fileRef = useRef(null);

  const ready = category && (category !== 'other' || otherLabel.trim().length > 0);

  function onFileChosen(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length || !ready) return;
    for (const file of files) {
      onAdd({ file, category, otherLabel: category === 'other' ? otherLabel.trim() : null });
    }
  }

  function chooseFiles() {
    if (!ready) return;
    // Post photos / training certificates carry per-file detail — capture it
    // with the file rather than sending a bare upload.
    if (categoryNeedsMetadata(category)) { setMetaOpen(true); return; }
    fileRef.current?.click();
  }

  return (
    <div style={{
      border: '1px solid var(--brand)', borderRadius: 10,
      background: 'var(--brand-weak)', padding: 14,
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-ink)' }}>Add attachment</div>

      <div className="field" style={{ margin: 0 }}>
        <label style={{ fontSize: 12 }}>What is this file? <span style={{ color: 'var(--danger)' }}>*</span></label>
        <SearchableSelect
          name="picker-category"
          value={category}
          onChange={e => { setCategory(e.target.value); if (e.target.value !== 'other') setOtherLabel(''); }}
          placeholder="Select category…"
          options={attachmentOptions.map(opt => ({
            value: opt.key,
            label: opt.label,
            sub:   requiredSet.has(opt.key) ? 'Required for this case' : undefined,
          }))}
        />
      </div>

      {category === 'other' && (
        <div className="field" style={{ margin: 0 }}>
          <label style={{ fontSize: 12 }}>Describe the file <span style={{ color: 'var(--danger)' }}>*</span></label>
          <input className="input" autoFocus
            placeholder="e.g. X-ray, Side photo, Witness statement"
            value={otherLabel} onChange={e => setOtherLabel(e.target.value)} />
          <div className="field-hint">Used in the filename (PascalCase, e.g. "side photo" → SidePhoto)</div>
        </div>
      )}

      <input ref={fileRef} type="file" multiple style={{ display: 'none' }} onChange={onFileChosen} />
      {metaOpen && (
        <SlotUploadModal
          open
          category={category}
          label={ATTACHMENT_CATEGORY_LABELS[category] || category}
          incidentDate={incidentDate || null}
          onCancel={() => setMetaOpen(false)}
          onConfirm={({ files, metadata }) => {
            setMetaOpen(false);
            for (const file of files) onAdd({ file, category, otherLabel: null, metadata });
          }}
        />
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
        <button type="button" className="btn ghost" onClick={onCancel}>Cancel</button>
        <button type="button" className="btn primary" disabled={!ready} onClick={chooseFiles}>
          Choose files
        </button>
      </div>
    </div>
  );
}

/** Date + time for matrix extra fields (e.g. incident_datetime). Date uses DateInput. */
// DateTimeExtraField moved to components/DateTimeExtraField.jsx (shared with the
// case-file Edit Incident Details modal).
